import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { supabase } from "../supabase";
import { toast } from "../Toast";
import { API_URL, DEV_MODE } from "../config/env";
import { AGENT_NAME_KEY, AGENT_SIGNOFF_KEY, COMPANY_NAME_KEY, LOGO_KEY, HEADSHOT_KEY, WHATS_NEW_KEY, WHATS_NEW_VERSION } from "../config/constants";
import { SIDEBAR_GROUPS, TONES, BUYER_STAGES } from "../config/ui";
import { safeParse, safeSet, loadContacts, genId } from "../lib/storage";
import { printableExport, exportCmaReport } from "../lib/export";
import { track, identify } from "../lib/analytics";
import { getUsageLimit } from "../lib/subscription";
import { supaFetch } from "../lib/supaFetch";
import { addressMatchKey } from "../lib/addressMatch";
import { styles } from "../styles/appStyles";
import AccountPage from "./AccountPage";
import ComingSoonPage from "./ComingSoonPage";
import AddressInput from "./AddressInput";
import DemoLockedPanel from "./DemoLockedPanel";
import { buildGmailUrl, buildOutlookUrl, buildMailtoUrl } from "../lib/composeEmail";

// Lazy-loaded: ContactsPage (+ its papaparse dep) only mounts on the Contacts tab; the two modals
// only mount on a specific user action. Keeping them out of the main shell chunk shrinks first load.
const ContactsPage = lazy(() => import("./ContactsPage"));
const WhatsNewModal = lazy(() => import("./WhatsNewModal"));
const BatchSendModal = lazy(() => import("./BatchSendModal"));

// Word-by-word output reveal (feature G). Splits generated copy into per-word spans with a cascading
// opacity fade via animation-delay. display:inline + opacity-only means ZERO layout/whitespace change
// vs a plain div (and .out-text already sets white-space:pre-wrap). Reduced motion is honored by the
// `.reveal-words` rule in appStyles (forces opacity:1 / animation:none). NOT used on CMA/Property Info.
function OutText({ children, className = "out-text", style }) {
  const text = typeof children === "string" ? children : (children == null ? "" : String(children));
  const tokens = text.match(/\S+\s*/g);
  if (!tokens) return <div className={`${className} reveal-words`} style={style}>{children}</div>;
  return (
    <div className={`${className} reveal-words`} style={style}>
      {tokens.map((tok, i) => (
        <span key={i} className="rw" style={{ animationDelay: `${Math.min(i * 0.03, 1.6)}s` }}>{tok}</span>
      ))}
    </div>
  );
}

// Fair Housing compliance banner — renders the flags from POST /fair-housing-scan above a
// generated output. Read-only guidance (phrase · protected class · compliant suggestion);
// the agent edits their copy then re-generates to re-check. Guidance, not legal advice.
function ComplianceBanner({ flags, onDismiss }) {
  const [open, setOpen] = useState(true);
  if (!flags || !flags.length) return null;
  const blocks = flags.filter((f) => f.severity === "block").length;
  return (
    <div className="fh-banner">
      <button type="button" className="fh-banner-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="fh-banner-title">
          ⚠ {flags.length} phrase{flags.length > 1 ? "s" : ""} may risk Fair Housing{blocks > 0 ? ` · ${blocks} high-risk` : ""}
        </span>
        <span className="fh-banner-toggle">{open ? "Hide" : "Review"}</span>
      </button>
      {open && (
        <div className="fh-banner-body">
          {flags.map((f, i) => (
            <div className="fh-flag" key={i}>
              <span className={`fh-sev fh-sev-${f.severity}`}>{f.severity === "block" ? "Avoid" : "Caution"}</span>
              <div className="fh-flag-text">
                <span className="fh-phrase">&ldquo;{f.phrase}&rdquo;</span> <span className="fh-class">{f.protected_class}</span>
                <div className="fh-sugg">&rarr; {f.suggestion}</div>
              </div>
            </div>
          ))}
          <div className="fh-banner-foot">
            <span className="fh-disclaimer">Guidance, not legal advice &middot; edit your copy, then re-generate to re-check.</span>
            <button type="button" className="fh-dismiss" onClick={onDismiss}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}

// MLS public-remarks character limits by board (Phase-0 research; confirmed boards + a safe
// 1,000 default for "Other/unknown"). Limits drift and several boards don't publish a count,
// so these are editable defaults, not gospel. https://dd.reso.org/DD2.0/Property/PublicRemarks/
const MLS_LIMITS = [
  { name: "Other / not sure (safe 1,000)", limit: 1000 },
  { name: "Bright MLS (~1,000)", limit: 1000 },
  { name: "CRMLS (1,500)", limit: 1500 },
  { name: "Stellar MLS (1,500)", limit: 1500 },
  { name: "FMLS / First MLS (800)", limit: 800 },
  { name: "Georgia MLS (4,000)", limit: 4000 },
  { name: "BeachesMLS (800)", limit: 800 },
  { name: "MRED / Chicago (1,000)", limit: 1000 },
  { name: "NWMLS (~1,000)", limit: 1000 },
  { name: "ACTRIS / Austin (1,024)", limit: 1024 },
  { name: "MLSListings (~1,300)", limit: 1300 },
  { name: "MetroList / Sacramento (~1,300)", limit: 1300 },
  { name: "HAR / Houston (~2,000)", limit: 2000 },
  { name: "CCIMLS / Cape Cod (1,200)", limit: 1200 },
  { name: "RESO standard (4,000)", limit: 4000 },
];

export default
function PitcherlyApp({ subscription, userId, userEmail, onLogout, isDemoMode = false, onExitDemo }) {
  const [tab, setTab] = useState("listing");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Tracks the ≤768px breakpoint, reactively. Used to keep the mobile-only Web Share button
  // off desktop (navigator.share exists on some desktop browsers, e.g. Safari/Edge).
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const DEMO_GEN_CAP = 5;
  const [demoGenUsed, setDemoGenUsed] = useState(() => {
    if (!isDemoMode) return 0;
    try { return parseInt(sessionStorage.getItem("demo_generations_used") || "0", 10) || 0; } catch { return 0; }
  });
  const demoCapReached = isDemoMode && demoGenUsed >= DEMO_GEN_CAP;

  // Show What's New modal once per version
  useEffect(() => {
    try {
      const seen = localStorage.getItem(WHATS_NEW_KEY);
      if (seen !== WHATS_NEW_VERSION) {
        const t = setTimeout(() => setWhatsNewOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);
  const dismissWhatsNew = () => {
    try { localStorage.setItem(WHATS_NEW_KEY, WHATS_NEW_VERSION); } catch {}
    setWhatsNewOpen(false);
  };

  // Close mobile sidebar when viewport grows above 768px; keep isMobile in sync.
  useEffect(() => {
    const handleResize = () => {
      const wide = window.innerWidth > 768;
      if (wide) setSidebarOpen(false);
      setIsMobile(!wide);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Identify real (non-demo) users for analytics funnels/retention. No-op without a PostHog key;
  // demo users are skipped inside identify().
  useEffect(() => {
    if (isDemoMode || !userId) return;
    identify(userId, { email: userEmail, plan: subscription?.plan });
  }, [userId, isDemoMode, userEmail, subscription?.plan]);

  // Lock body scroll while the mobile sidebar is open (mobile only)
  useEffect(() => {
    if (window.innerWidth > 768) return;
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const [tone, setTone] = useState("Luxury");
  const [customToneOpen, setCustomToneOpen] = useState(false);
  const [customToneInput, setCustomToneInput] = useState("");
  const [activeToneLabel, setActiveToneLabel] = useState("Luxury");
  const [stage, setStage] = useState("Ready to buy");
  const [loading, setLoading] = useState(false);
  const [outTab, setOutTab] = useState("mls");
  const [copied, setCopied] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);

  // Agent name from localStorage
  const [savedAgentName, setSavedAgentName] = useState(() => localStorage.getItem(AGENT_NAME_KEY) || "James");
  const [savedSignoff, setSavedSignoff] = useState(() => localStorage.getItem(AGENT_SIGNOFF_KEY) || "Best");
  // PDF branding (per-device; surfaced on Save-PDF exports). Optional — exports render fine
  // with neither set (Pitcherly-only branding).
  const [companyName, setCompanyName] = useState(() => { try { return localStorage.getItem(COMPANY_NAME_KEY) || ""; } catch { return ""; } });
  const [logoData, setLogoData] = useState(() => { try { return localStorage.getItem(LOGO_KEY) || ""; } catch { return ""; } });
  const [headshotData, setHeadshotData] = useState(() => { try { return localStorage.getItem(HEADSHOT_KEY) || ""; } catch { return ""; } });
  const logoInputRef = useRef(null);
  const headshotInputRef = useRef(null);
  // Cross-device agent-profile sync: gate auto-save until the load-time hydration ATTEMPT
  // finishes, and debounce PUTs so we don't write on every keystroke.
  const profileSaveTimer = useRef(null);
  const profileLastSyncedRef = useRef(null); // last profile values known to be on the server
  // True once hydration has ATTEMPTED to load the server profile (success, failure, or 401).
  // Gates the debounced save so a mount-time write can't clobber the server — but it flips even
  // on a failed/401 GET, so explicit edits always sync (never permanently gated). State, not a
  // ref, so flipping it re-runs the save effect and flushes any edit made during the load window.
  const [profileHydrated, setProfileHydrated] = useState(false);

  // Shared property state (synced across tabs)
  const [sharedProperty, setSharedProperty] = useState({
    address: "", bedrooms: "", bathrooms: "", sqft: "",
    price: "", features: "",
  });

  // Usage counter — backed by server-side user_outputs table
  const planLimit = getUsageLimit(subscription);
  const isUnlimited = subscription?.plan === "pro" || DEV_MODE;
  const isTrial = subscription?.stripe_status === "trialing";
  // CMA market data (RentCast AVM) is a Starter/Pro feature — same gate as property lookups.
  const cmaHasLookups = !isTrial && (subscription?.plan === "starter" || subscription?.plan === "pro");
  const usageCacheKey = userId ? `pitcherly_usage_${userId}` : null;
  const [usageCount, setUsageCount] = useState(() => {
    if (!usageCacheKey) return 0;
    const cached = localStorage.getItem(usageCacheKey);
    return cached ? parseInt(cached, 10) || 0 : 0;
  });
  const [usageLoading, setUsageLoading] = useState(true);
  const usageFetchedRef = useRef(false);
  const usesRemaining = isUnlimited ? Infinity : Math.max(0, planLimit - usageCount);

  // Fetch usage count from backend — called ONCE after auth is ready
  useEffect(() => {
    if (isDemoMode) { setUsageLoading(false); usageFetchedRef.current = true; return; }
    if (!userId || !subscription?.stripe_status) return;
    if (usageFetchedRef.current) return;
    usageFetchedRef.current = true;
    const status = subscription.stripe_status;
    const url = `${API_URL}/get-usage?user_id=${encodeURIComponent(userId)}&status=${encodeURIComponent(status)}`;
    (async () => {
      const token = await getToken();
      fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        .then(r => r.json())
        .then(data => {
          const count = data?.count || 0;
          setUsageCount(count);
          if (usageCacheKey) localStorage.setItem(usageCacheKey, String(count));
          setUsageLoading(false);
        })
        .catch(e => {
          console.error("fetchUsage error:", e);
          setUsageLoading(false);
        });
    })();
  }, [userId, subscription?.stripe_status, usageCacheKey, isDemoMode]);

  const logGeneration = useCallback(async (tabName) => {
    if (!userId) return;
    if (isDemoMode) {
      setDemoGenUsed(prev => {
        const next = prev + 1;
        try { sessionStorage.setItem("demo_generations_used", String(next)); } catch {}
        return next;
      });
      return;
    }
    // Optimistic bump
    setUsageCount(prev => {
      const next = prev + 1;
      if (usageCacheKey) localStorage.setItem(usageCacheKey, String(next));
      return next;
    });
    // Then reconcile with server (source of truth) to avoid drift under rapid clicks
    const status = subscription?.stripe_status;
    if (!status) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/get-usage?user_id=${encodeURIComponent(userId)}&status=${encodeURIComponent(status)}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (!r.ok) return;
      const d = await r.json();
      const real = d?.count;
      if (typeof real === "number") {
        setUsageCount(real);
        if (usageCacheKey) localStorage.setItem(usageCacheKey, String(real));
      }
    } catch (_) { /* optimistic value stays */ }
  }, [userId, usageCacheKey, subscription?.stripe_status, isDemoMode]);

  // Form fields
  const [address, setAddress] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [halfBaths, setHalfBaths] = useState("0");
  const [sqft, setSqft] = useState("");
  const [price, setPrice] = useState("");
  const [features, setFeatures] = useState("");
  const [propertySaved, setPropertySaved] = useState(false);

  // Email handoff helpers
  const notifyIfTruncated = (truncated) => {
    if (truncated) toast.info('Email was long — the draft was truncated. Use "Copy full email" to paste the rest.');
  };
  const sendViaGmail = (subject, body, to) => {
    const { url, truncated } = buildGmailUrl({ to, subject, body, authuser: userEmail });
    window.open(url, "_blank", "noopener");
    notifyIfTruncated(truncated);
  };
  const sendViaOutlook = (subject, body, to) => {
    const { url, truncated } = buildOutlookUrl({ to, subject, body });
    window.open(url, "_blank", "noopener");
    notifyIfTruncated(truncated);
  };

  const [emailTo, setEmailTo] = useState("");
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [emailPickerSelected, setEmailPickerSelected] = useState(() => new Set());
  const [emailPickerSearch, setEmailPickerSearch] = useState("");
  const [batchEmail, setBatchEmail] = useState(null); // {subject, body} when the "Send to multiple" modal is open
  const renderSendEmail = (subject, body, toEmail) => {
    const multiEmails = selectedContacts.length > 0 ? selectedContacts.map(c => c.email).filter(Boolean).join(", ") : "";
    const effectiveTo = emailTo || toEmail || multiEmails || contactEmailRef.current || "";
    const { url: mailto, truncated: mailtoTruncated } = buildMailtoUrl({ to: effectiveTo, subject, body });
    const allContacts = loadContacts().filter(c => c.email && c.email.includes("@"));
    const q = emailPickerSearch.toLowerCase();
    const filteredContacts = allContacts.filter(c => {
      if (!q) return true;
      const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      return name.includes(q) || (c.email || "").toLowerCase().includes(q);
    });
    const confirmEmailPicker = () => {
      const picked = allContacts.filter(c => emailPickerSelected.has(c.id));
      const existing = (emailTo || "").split(",").map(s => s.trim()).filter(Boolean);
      const toAdd = picked.map(c => c.email).filter(e => !existing.includes(e));
      const merged = [...existing, ...toAdd].join(", ");
      setEmailTo(merged);
      setEmailPickerOpen(false);
      setEmailPickerSearch("");
      setEmailPickerSelected(new Set());
    };
    return (
      <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:8,position:'relative'}}>
          <label style={{fontSize:12,color:'#888',fontWeight:600,whiteSpace:'nowrap'}}>To:</label>
          <input
            value={emailTo || toEmail || multiEmails || contactEmailRef.current || ""}
            onChange={e => setEmailTo(e.target.value)}
            placeholder="recipient@email.com"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            style={{flex:1,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',fontSize:12,outline:'none',fontFamily:"'Geist', sans-serif"}}
          />
          <button
            type="button"
            onClick={() => { setEmailPickerOpen(o => !o); setEmailPickerSearch(""); setEmailPickerSelected(new Set()); }}
            title="Import from Contacts"
            style={{padding:'7px 10px',background:'transparent',border:'1px solid #2a2a2a',color:'#d4915c',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap',fontFamily:"'Geist', sans-serif"}}
          >
            👥 Contacts
          </button>
          {emailPickerOpen && (
            <div
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); confirmEmailPicker(); } if (e.key === "Escape") setEmailPickerOpen(false); }}
              style={{position:'absolute',top:'100%',right:0,marginTop:6,zIndex:100,background:'#161616',border:'1px solid #2a2a2a',borderRadius:8,width:'min(320px, calc(100vw - 24px))',boxShadow:'0 8px 24px rgba(0,0,0,0.5)'}}
            >
              <div style={{padding:10,borderBottom:'1px solid #2a2a2a'}}>
                <input
                  autoFocus
                  placeholder="Search contacts..."
                  value={emailPickerSearch}
                  onChange={e => setEmailPickerSearch(e.target.value)}
                  style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',outline:'none',fontFamily:"'Geist', sans-serif",boxSizing:'border-box'}}
                />
              </div>
              <div style={{maxHeight:240,overflowY:'auto'}}>
                {filteredContacts.length === 0 && (
                  <div style={{padding:'14px 12px',fontSize:12,color:'#888',textAlign:'center'}}>
                    {allContacts.length === 0 ? "No contacts with emails yet." : "No matches."}
                  </div>
                )}
                {filteredContacts.map(c => {
                  const checked = emailPickerSelected.has(c.id);
                  return (
                    <label key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',cursor:'pointer',borderBottom:'1px solid #1f1f1f',background:checked ? '#1f1410' : 'transparent'}}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setEmailPickerSelected(prev => {
                            const next = new Set(prev);
                            if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                            return next;
                          });
                        }}
                        style={{accentColor:'#d4915c'}}
                      />
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,color:'#e8e4dd',fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {(c.firstName || "") + " " + (c.lastName || "")}
                        </div>
                        <div style={{fontSize:11,color:'#888',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {c.email}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div style={{display:'flex',gap:8,padding:10,borderTop:'1px solid #2a2a2a'}}>
                <button
                  type="button"
                  onClick={() => setEmailPickerOpen(false)}
                  style={{flex:1,padding:'7px 10px',background:'transparent',border:'1px solid #2a2a2a',color:'#888',borderRadius:6,cursor:'pointer',fontSize:12,fontFamily:"'Geist', sans-serif"}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmEmailPicker}
                  disabled={emailPickerSelected.size === 0}
                  style={{flex:2,padding:'7px 10px',background:emailPickerSelected.size === 0 ? '#2a2a2a' : 'var(--accent)',border:'none',color:'#fff',borderRadius:6,cursor:emailPickerSelected.size === 0 ? 'not-allowed' : 'pointer',fontSize:12,fontWeight:600,fontFamily:"'Geist', sans-serif"}}
                >
                  Add {emailPickerSelected.size > 0 ? `${emailPickerSelected.size} ` : ""}Selected
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{
          padding: '8px 12px',
          background: 'rgba(212, 145, 92, 0.08)',
          border: '1px solid rgba(212, 145, 92, 0.2)',
          borderRadius: 6,
          fontSize: 11,
          color: '#a89789',
          lineHeight: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
        }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>ℹ️</span>
          <span>
            These buttons open your draft in your own email — Gmail, Outlook, or your default mail app.
            The email sends from <strong>{userEmail || "your logged-in email"}</strong> (or whichever
            account is signed in there). Pitcherly never sends emails on your behalf — review and hit Send yourself.
          </span>
        </div>
        <button
          className="email-send-btn"
          onClick={() => { window.location.href = mailto; notifyIfTruncated(mailtoTruncated); }}
          style={{width:'100%',padding:'10px 16px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:8,fontFamily:"'Geist', sans-serif"}}
        >
          📧 Open in Email
        </button>
        <button
          className="email-send-btn"
          onClick={() => sendViaGmail(subject, body, effectiveTo)}
          style={{width:'100%',padding:'8px 16px',background:'transparent',border:'1px solid #d4915c',color:'#d4915c',borderRadius:8,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',gap:8,fontFamily:"'Geist', sans-serif"}}
        >
          Open in Gmail
        </button>
        <button
          className="email-send-btn"
          onClick={() => sendViaOutlook(subject, body, effectiveTo)}
          style={{width:'100%',padding:'8px 16px',background:'transparent',border:'1px solid #2a2a2a',color:'#888',borderRadius:8,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',gap:8,fontFamily:"'Geist', sans-serif"}}
        >
          Open in Outlook
        </button>
        <button
          className="act-btn"
          onClick={() => copy(`Subject: ${subject || ""}\n\n${body || ""}`)}
          style={{width:'100%',marginTop:2}}
        >
          {copied ? "✓ Copied" : "Copy full email"}
        </button>
        {!isDemoMode && (
          <button
            className="act-btn"
            onClick={() => setBatchEmail({ subject: subject || "", body: body || "" })}
            style={{width:'100%',marginTop:2}}
          >
            👥 Send to multiple…
          </button>
        )}
      </div>
    );
  };

  // Helper to get auth token
  const getToken = async () => {
    if (DEV_MODE || !supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  };

  // All of this user's Supabase user_properties rows (the listing-form store) — kept so the
  // duplicate-lookup guard can match against BOTH stores, and so re-lookups can update the
  // matched row in place by id.
  const [supaProps, setSupaProps] = useState([]);

  // Load saved property from Supabase on mount
  useEffect(() => {
    if (DEV_MODE) return;
    (async () => {
      const token = await getToken();
      if (!token) return;
      const data = await supaFetch(`/user_properties?user_id=eq.${userId}&select=*`, token);
      if (Array.isArray(data)) setSupaProps(data);
      if (data && data.length > 0) {
        const p = data[0];
        setAddress(p.address || ""); setBedrooms(p.bedrooms || ""); setBathrooms(p.bathrooms || "");
        setHalfBaths(p.half_baths || "0"); setSqft(p.sqft || ""); setPrice(p.price || "");
        setFeatures(p.features || "");
        setSharedProperty({
          address: p.address || "", bedrooms: p.bedrooms || "", bathrooms: p.bathrooms || "",
          sqft: p.sqft || "", price: p.price || "", features: p.features || "",
        });
      }
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Agent profile cross-device sync ───
  // Source of truth is the account (Supabase user_profiles); localStorage is the cache. On load
  // we GET the profile and hydrate state (server wins for fields the user hasn't touched). The
  // GET can race ahead of the Supabase session on mount (token not ready yet → 401), so we RETRY
  // a few times until the session settles rather than giving up — the token is valid once ready
  // (the save PUT proves it). On a genuine success with no row, one-time seed of customized local
  // settings. The hydration ATTEMPT always flips profileHydrated (even on persistent failure) so
  // explicit edits always sync; but we only PROACTIVELY seed on a real success, never on a
  // failed/401 load (F4 — no clobber of an unread row). Demo users + dev mode skip entirely.
  useEffect(() => {
    if (DEV_MODE || isDemoMode || !userId || String(userId).startsWith("demo-")) return;
    // Mount snapshot (from STATE, not localStorage): preserves an edit made during the load
    // window (server only wins for fields the user hasn't touched — prev !== mount) AND is the
    // sync baseline. Keying the baseline off the same state the save effect compares against
    // means a dropped localStorage write (quota) can't make baseline diverge from cur and trigger
    // a spurious proactive PUT (F4).
    const mount = { name: savedAgentName, signoff: savedSignoff, company: companyName, logo: logoData, headshot: headshotData };
    const mountProfile = {
      display_name: mount.name || "", company_name: mount.company || "",
      email_signoff: mount.signoff || "", logo: mount.logo || "", headshot: mount.headshot || "",
    };
    let cancelled = false;
    (async () => {
      try {
        // Retry the GET until it returns a non-401 status (or attempts run out): handles the mount
        // race where the session/token isn't ready yet. The token is valid once ready (the PUT proves it).
        let res = null, goodToken = null;
        for (let attempt = 0; attempt < 5 && !cancelled; attempt++) {
          let token = null;
          try { token = await getToken(); } catch { /* session machinery not ready → retry */ }
          if (token) {
            try {
              const r = await fetch(`${API_URL}/profile`, { headers: { Authorization: `Bearer ${token}` } });
              if (r.status !== 401) { res = r; goodToken = token; break; } // 200 / non-auth status → done
            } catch { /* network blip → retry */ }
          }
          if (attempt < 4) await new Promise(resolve => setTimeout(resolve, 600)); // settle, then retry
        }
        if (cancelled) return;
        let row = null;
        if (res && res.ok) { try { row = await res.json(); } catch { row = null; } }
        if (cancelled) return; // userId may have switched during res.json() — don't write stale data
        const hasServer = row && (row.display_name || row.company_name || row.email_signoff || row.logo || row.headshot);
        if (hasServer) {
          // Server wins, but preserve any edit made during the load window (prev !== mount).
          // Setting state cascades to localStorage via the existing sync effects (cache stays correct).
          setSavedAgentName(prev => prev === mount.name ? (row.display_name || "") : prev);
          setSavedSignoff(prev => prev === mount.signoff ? (row.email_signoff || "") : prev);
          setCompanyName(prev => prev === mount.company ? (row.company_name || "") : prev);
          setLogoData(prev => prev === mount.logo ? (row.logo || "") : prev);
          setHeadshotData(prev => prev === mount.headshot ? (row.headshot || "") : prev);
          // Baseline = server values → the save effect skips a redundant write but still PUTs a
          // preserved in-flight edit (which differs from these).
          profileLastSyncedRef.current = {
            display_name: row.display_name || "", company_name: row.company_name || "",
            email_signoff: row.email_signoff || "", logo: row.logo || "", headshot: row.headshot || "",
          };
        } else if (res && res.ok) {
          // Genuine success with no row → one-time seed of genuinely-customized settings
          // (don't persist the "James"/"Best" placeholder defaults — F6).
          const customized = (mountProfile.display_name && mountProfile.display_name !== "James")
            || (mountProfile.email_signoff && mountProfile.email_signoff !== "Best")
            || mountProfile.company_name || mountProfile.logo || mountProfile.headshot;
          if (customized && goodToken && !cancelled) {
            try {
              const sres = await fetch(`${API_URL}/profile`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${goodToken}` },
                body: JSON.stringify(mountProfile),
              });
              // null baseline on a failed seed → the profileHydrated flip re-attempts the PUT.
              profileLastSyncedRef.current = sres.ok ? { ...mountProfile } : null;
            } catch { profileLastSyncedRef.current = null; }
          } else {
            // Nothing worth seeding (defaults) → baseline = mount so we don't push; first edit syncs.
            profileLastSyncedRef.current = { ...mountProfile };
          }
        } else {
          // FAILED load (no token / persistent 401 / network). F4 guard: do NOT proactively push.
          // Baseline = mount (state-derived) so the flip fires no PUT until the user actually edits.
          profileLastSyncedRef.current = { ...mountProfile };
        }
      } catch (e) {
        console.warn("profile hydration failed:", e);
      } finally {
        // Attempt complete (any outcome — incl. a thrown getToken/parse) → un-gate saves so
        // explicit edits always sync. Saves are NEVER permanently gated.
        if (!cancelled) setProfileHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist agent-profile edits to the account (debounced), alongside the localStorage sync
  // effects above. Gated until a successful server load so a mount-time (or post-failure) write
  // can't clobber the server. Skips a PUT when nothing changed since the last sync (avoids the
  // redundant write right after hydration). Demo/dev skip (no account).
  useEffect(() => {
    if (DEV_MODE || isDemoMode || !userId || String(userId).startsWith("demo-")) return;
    if (!profileHydrated) return;
    const cur = {
      display_name: savedAgentName || "", company_name: companyName || "",
      email_signoff: savedSignoff || "", logo: logoData || "", headshot: headshotData || "",
    };
    const last = profileLastSyncedRef.current;
    if (last && last.display_name === cur.display_name && last.company_name === cur.company_name
      && last.email_signoff === cur.email_signoff && last.logo === cur.logo && last.headshot === cur.headshot) {
      return; // nothing changed since the last server sync — skip the redundant PUT
    }
    if (profileSaveTimer.current) clearTimeout(profileSaveTimer.current);
    profileSaveTimer.current = setTimeout(async () => {
      const token = await getToken();
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/profile`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(cur),
        });
        if (res.ok) {
          profileLastSyncedRef.current = cur; // remember what's now on the server
        } else if (res.status === 413 || res.status === 422) {
          toast.error("Logo/headshot too large to sync across devices — saved on this device only.");
        } else {
          console.warn(`profile sync PUT failed: ${res.status}`);
        }
      } catch (e) { console.warn("profile sync failed:", e); }
    }, 800);
    return () => { if (profileSaveTimer.current) clearTimeout(profileSaveTimer.current); };
  }, [savedAgentName, savedSignoff, companyName, logoData, headshotData, userId, isDemoMode, profileHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow-up form fields
  const [leadName, setLeadName] = useState("Sarah");
  const [agentName, setAgentName] = useState(savedAgentName);
  const [leadContext, setLeadContext] = useState("");

  // Contract form field
  const [contractText, setContractText] = useState(`Purchase and Sale Agreement dated March 3, 2026.\nBuyer: Alex P. Carter. Seller: Morgan L. Bennett.\nProperty: 742 Evergreen Terrace, Springfield, IL 62704.\nPurchase price: $485,000. Earnest money: $7,500 due within 3 business days.\nFinancing contingency: 21 days. Inspection period: 10 days.\nAppraisal contingency: 17 days.\nClosing date: April 15, 2026.\nSeller to credit buyer $4,000 toward closing costs per Section 8.2.\nSection 14.3: Buyer waives right to final walkthrough within 48 hours of closing...`);

  // Inspection report form field (paste raw report text → severity-triaged summary)
  const [reportText, setReportText] = useState(`Home Inspection Report — 742 Evergreen Terrace, Springfield, IL
Inspector: BuildRight Inspections, License #4471. Date: March 5, 2026.

ROOF: Asphalt shingles ~18 years old, near end of service life. Cracked/missing shingles above the garage. Active moisture staining on attic decking below.
ELECTRICAL: Federal Pacific Stab-Lok panel present — breakers known to fail to trip (fire hazard). Recommend evaluation/replacement by a licensed electrician. Three ungrounded outlets in living room.
PLUMBING: Original galvanized supply lines in basement showing corrosion; low hot-water pressure upstairs. Minor active drip at kitchen sink P-trap.
FOUNDATION/STRUCTURE: Two hairline vertical cracks in poured basement wall, no displacement, appears stable; recommend monitoring.
HVAC: Furnace 14 years old, operational; heat exchanger could not be fully inspected. AC condenser fins bent on north side.
WATER/MOISTURE: Efflorescence on basement wall near downspout; grading slopes toward the house on the east side. Improve grading and extend downspouts.
SAFETY: Smoke detectors missing/expired on two levels. Stair handrail loose at the top landing. Water heater TPR discharge tube terminates too high.
EXTERIOR/INTERIOR: Wood trim rot at two window sills; peeling paint on south fascia; cosmetic drywall cracks at door corners; one stuck bedroom window.`);

  // Open house form fields
  const [ohDate, setOhDate] = useState("");
  const [ohVisitorName, setOhVisitorName] = useState("");
  const [ohNotes, setOhNotes] = useState("");

  // Seller update form fields
  const [sellerName, setSellerName] = useState("");
  const [daysOnMarket, setDaysOnMarket] = useState("");
  const [showingsThisWeek, setShowingsThisWeek] = useState("");
  const [showingFeedback, setShowingFeedback] = useState("");
  const [priceReduction, setPriceReduction] = useState(false);

  // Offer summary form fields — fact/finance-only; the Fair Housing scanner runs on the output
  const [buyerName, setBuyerName] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [earnestMoney, setEarnestMoney] = useState("");
  const [financing, setFinancing] = useState("Conventional, pre-approved");
  const [contingencies, setContingencies] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [offerState, setOfferState] = useState("");
  const [offerNotes, setOfferNotes] = useState("");

  // Bio form fields
  const [bioYears, setBioYears] = useState("");
  const [bioMarket, setBioMarket] = useState("");
  const [bioSpecialties, setBioSpecialties] = useState([]);
  const [bioFact, setBioFact] = useState("");
  const [bioTone, setBioTone] = useState("Professional");

  // Just Sold form fields
  const [jsSalePrice, setJsSalePrice] = useState("");
  const [jsDaysOnMarket, setJsDaysOnMarket] = useState("");
  const [jsFunFact, setJsFunFact] = useState("");
  const [jsLoading, setJsLoading] = useState(false);
  const [justSoldResult, setJustSoldResult] = useState(null);
  const [jsOutTab, setJsOutTab] = useState("social");

  // Expired form fields
  const [expiredName, setExpiredName] = useState("");
  const [expiredPrice, setExpiredPrice] = useState("");
  const [expiredDays, setExpiredDays] = useState("");

  // FSBO form fields
  const [fsboName, setFsboName] = useState("");
  const [fsboPrice, setFsboPrice] = useState("");
  const [fsboContext, setFsboContext] = useState("");

  // CMA form fields
  const [cmaMarket, setCmaMarket] = useState("");
  const [useMarketData, setUseMarketData] = useState(true);
  const [cmaMode, setCmaMode] = useState("seller"); // "seller" (default) | "buyer"

  // Contacts integration
  const [contactPickerFor, setContactPickerFor] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const contactEmailRef = useRef("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const MULTI_SELECT_TABS = ["followup", "openhouse", "expired", "fsbo"];

  const onUseContact = (contact, targetTab) => {
    if (!contact) return;
    const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    contactEmailRef.current = contact.email || "";
    setEmailTo(contact.email || ""); // keep the To: field/state in sync on single-select pick (P2-3)
    if (targetTab === "followup") {
      setLeadName(fullName);
      if (contact.address) setSharedProperty(p => ({ ...p, address: contact.address }));
    } else if (targetTab === "openhouse") {
      setOhVisitorName(fullName);
      if (contact.address) setSharedProperty(p => ({ ...p, address: contact.address }));
    } else if (targetTab === "sellerupdate") {
      setSellerName(fullName);
      if (contact.address) setSharedProperty(p => ({ ...p, address: contact.address }));
    } else if (targetTab === "expired") {
      setExpiredName(fullName);
      if (contact.address) setSharedProperty(p => ({ ...p, address: contact.address }));
    } else if (targetTab === "fsbo") {
      setFsboName(fullName);
      if (contact.address) setSharedProperty(p => ({ ...p, address: contact.address }));
    }
    setTab(targetTab);
  };

  const applySelectedContacts = (contacts, forTab) => {
    if (contacts.length === 0) return;
    const first = contacts[0];
    const fullName = `${first.firstName || ""} ${first.lastName || ""}`.trim();
    const allEmails = contacts.map(c => c.email).filter(Boolean).join(", ");
    contactEmailRef.current = allEmails;
    setEmailTo(allEmails);
    if (forTab === "followup") {
      setLeadName(fullName);
      if (first.address) setSharedProperty(p => ({ ...p, address: first.address }));
    } else if (forTab === "openhouse") {
      setOhVisitorName(fullName);
      if (first.address) setSharedProperty(p => ({ ...p, address: first.address }));
    } else if (forTab === "expired") {
      setExpiredName(fullName);
      if (first.address) setSharedProperty(p => ({ ...p, address: first.address }));
    } else if (forTab === "fsbo") {
      setFsboName(fullName);
      if (first.address) setSharedProperty(p => ({ ...p, address: first.address }));
    }
  };

  const pickContact = (contact) => {
    if (!contact || !contactPickerFor) return;
    // Single-select tabs: apply immediately and close
    onUseContact(contact, contactPickerFor);
    setSelectedContacts([{ id: contact.id, firstName: contact.firstName, lastName: contact.lastName, email: contact.email }]);
    setContactPickerFor(null);
    setContactSearch("");
  };

  const toggleMultiContact = (contact) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id);
      const next = exists ? prev.filter(c => c.id !== contact.id) : [...prev, { id: contact.id, firstName: contact.firstName, lastName: contact.lastName, email: contact.email }];
      return next;
    });
  };

  const removeSelectedContact = (id) => {
    setSelectedContacts(prev => {
      const next = prev.filter(c => c.id !== id);
      if (next.length > 0) {
        const allEmails = next.map(c => c.email).filter(Boolean).join(", ");
        contactEmailRef.current = allEmails;
        setEmailTo(allEmails);
      } else {
        contactEmailRef.current = "";
        setEmailTo("");
      }
      return next;
    });
  };

  const renderContactPicker = (forTab) => {
    const isMulti = MULTI_SELECT_TABS.includes(forTab);
    const allContacts = loadContacts();
    const isOpen = contactPickerFor === forTab;
    const q = contactSearch.toLowerCase();
    const filtered = allContacts.filter(c => !q || `${c.firstName} ${c.lastName} ${c.email}`.toLowerCase().includes(q));
    const selCount = selectedContacts.length;
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          className="picker-toggle"
          onClick={() => { setContactPickerFor(isOpen ? null : forTab); setContactSearch(""); }}
          style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Geist', sans-serif", whiteSpace: 'nowrap' }}
        >
          👥 From Contacts{isMulti && selCount > 0 ? ` (${selCount})` : ""}
        </button>
        {isOpen && (
          <div className="picker-dropdown" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(280px, calc(100vw - 24px))', marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
            <div className="sheet-handle" aria-hidden="true" />
            <div style={{ padding: 8 }}>
              <input
                autoFocus
                placeholder="Search contacts..."
                value={contactSearch}
                onChange={e => setContactSearch(e.target.value)}
                style={{ width: '100%', fontSize: 12, padding: '7px 10px', background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 6, color: '#e8e4dd', outline: 'none', fontFamily: "'Geist', sans-serif", boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: 250, overflowY: 'auto' }}>
              {filtered.length === 0 && <div style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>No contacts found</div>}
              {filtered.map(c => {
                const isSel = isMulti && selectedContacts.some(s => s.id === c.id);
                return (
                <div key={c.id}
                  onClick={() => isMulti ? toggleMultiContact(c) : pickContact(c)}
                  style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#e8e4dd', borderTop: '1px solid #1f1f1f', transition: 'background 0.1s', background: isSel ? 'var(--accent-dim)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#1f1f1f'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{c.firstName} {c.lastName}</div>
                    {c.email && <div style={{ fontSize: 11, color: '#666', marginTop: 1 }}>{c.email}</div>}
                  </div>
                  {isSel && <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14 }}>✓</span>}
                </div>
                );
              })}
            </div>
            {isMulti && (
              <div style={{ padding: '6px 8px', borderTop: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {selCount > 0 && <button onClick={() => { setSelectedContacts([]); contactEmailRef.current = ""; setEmailTo(""); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 11, fontFamily: "'Geist', sans-serif", padding: '4px 0' }}>Clear All</button>}
                {selCount === 0 && <span />}
                <button onClick={() => { applySelectedContacts(selectedContacts, forTab); setContactPickerFor(null); setContactSearch(""); }} style={{ padding: '5px 14px', background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Geist', sans-serif" }}>Done</button>
              </div>
            )}
          </div>
        )}
        {isMulti && selCount > 0 && !isOpen && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {selectedContacts.map(c => (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 11, color: 'var(--text)' }}>
                {c.firstName} {c.lastName}
                <button aria-label="Remove contact from selection" title="Remove" onClick={(e) => { e.stopPropagation(); removeSelectedContact(c.id); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Property picker (From Saved Properties)
  const [propPickerFor, setPropPickerFor] = useState(null);
  const [propPickerSearch, setPropPickerSearch] = useState("");

  const pickProperty = (prop, forTab) => {
    if (!prop) return;
    if (forTab === "listing") {
      setAddress(prop.address || "");
      if (prop.bedrooms) setBedrooms(String(prop.bedrooms));
      if (prop.bathrooms) setBathrooms(String(prop.bathrooms));
      setHalfBaths(prop.halfBaths || prop.half_baths || "0");
      if (prop.sqft) setSqft(String(prop.sqft));
      const featureBits = [];
      if (prop.yearBuilt) featureBits.push(`Built in ${prop.yearBuilt}`);
      if (prop.propertyType) featureBits.push(prop.propertyType);
      if (prop.lotSize) featureBits.push(`${prop.lotSize} sqft lot`);
      if (featureBits.length > 0) setFeatures(featureBits.join(", "));
      setSharedProperty(p => ({
        ...p,
        address: prop.address || p.address,
        bedrooms: prop.bedrooms ? String(prop.bedrooms) : p.bedrooms,
        bathrooms: prop.bathrooms ? String(prop.bathrooms) : p.bathrooms,
        sqft: prop.sqft ? String(prop.sqft) : p.sqft,
      }));
    } else if (forTab === "followup" || forTab === "openhouse" || forTab === "sellerupdate" || forTab === "expired" || forTab === "fsbo") {
      setSharedProperty(p => ({
        ...p,
        address: prop.address || p.address,
        bedrooms: prop.bedrooms ? String(prop.bedrooms) : p.bedrooms,
        bathrooms: prop.bathrooms ? String(prop.bathrooms) : p.bathrooms,
        sqft: prop.sqft ? String(prop.sqft) : p.sqft,
      }));
      const priceStr = prop.market_value?.estimate != null ? String(prop.market_value.estimate)
        : (prop.lastSalePrice != null && String(prop.lastSalePrice).trim() !== "") ? String(prop.lastSalePrice)
        : "";  // market value -> last sale -> blank; never the tax-assessed value
      if (forTab === "fsbo" && priceStr) setFsboPrice(String(priceStr));
      if (forTab === "expired" && priceStr) setExpiredPrice(String(priceStr));
    } else if (forTab === "justsold") {
      setSharedProperty(p => ({
        ...p,
        address: prop.address || p.address,
        bedrooms: prop.bedrooms ? String(prop.bedrooms) : p.bedrooms,
        bathrooms: prop.bathrooms ? String(prop.bathrooms) : p.bathrooms,
        sqft: prop.sqft ? String(prop.sqft) : p.sqft,
      }));
      const jsPrice = prop.market_value?.estimate != null ? String(prop.market_value.estimate)
        : (prop.lastSalePrice != null && String(prop.lastSalePrice).trim() !== "") ? String(prop.lastSalePrice) : "";
      if (jsPrice) setJsSalePrice(jsPrice);
    } else if (forTab === "cma") {
      setAddress(prop.address || "");
      if (prop.bedrooms) setBedrooms(String(prop.bedrooms));
      if (prop.bathrooms) setBathrooms(String(prop.bathrooms));
      if (prop.sqft) setSqft(String(prop.sqft));
      const featureBits = [];
      if (prop.yearBuilt) featureBits.push(`Built in ${prop.yearBuilt}`);
      if (prop.propertyType) featureBits.push(prop.propertyType);
      if (prop.lotSize) featureBits.push(`${prop.lotSize} sqft lot`);
      if (featureBits.length > 0) setFeatures(featureBits.join(", "));
      // Anchor the CMA on a real asking price — without this the price field is
      // left blank/stale and the model free-estimates a wrong-magnitude value.
      const priceStr = prop.market_value?.estimate != null ? String(prop.market_value.estimate)
        : (prop.lastSalePrice != null && String(prop.lastSalePrice).trim() !== "") ? String(prop.lastSalePrice)
        : "";  // market value -> last sale -> blank; never the tax-assessed value
      if (priceStr) setPrice(String(priceStr));
      setSharedProperty(p => ({
        ...p,
        address: prop.address || p.address,
        bedrooms: prop.bedrooms ? String(prop.bedrooms) : p.bedrooms,
        bathrooms: prop.bathrooms ? String(prop.bathrooms) : p.bathrooms,
        sqft: prop.sqft ? String(prop.sqft) : p.sqft,
      }));
    }
    setPropPickerFor(null);
    setPropPickerSearch("");
  };

  const renderPropertyPicker = (forTab) => {
    const allProps = loadProperties();
    const isOpen = propPickerFor === forTab;
    const q = propPickerSearch.toLowerCase();
    const filtered = allProps.filter(p => !q || (p.address || "").toLowerCase().includes(q));
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          className="picker-toggle"
          onClick={() => { setPropPickerFor(isOpen ? null : forTab); setPropPickerSearch(""); }}
          style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Geist', sans-serif", whiteSpace: 'nowrap' }}
        >
          📋 From Properties
        </button>
        {isOpen && (
          <div className="picker-dropdown" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(280px, calc(100vw - 24px))', marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
            <div className="sheet-handle" aria-hidden="true" />
            <div style={{ padding: 8 }}>
              <input
                autoFocus
                placeholder="Search properties..."
                value={propPickerSearch}
                onChange={e => setPropPickerSearch(e.target.value)}
                style={{ width: '100%', fontSize: 12, padding: '7px 10px', background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 6, color: '#e8e4dd', outline: 'none', fontFamily: "'Geist', sans-serif", boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: 250, overflowY: 'auto' }}>
              {allProps.length === 0 && <div style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>No saved properties — save one from the Property Info tab</div>}
              {allProps.length > 0 && filtered.length === 0 && <div style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>No matches</div>}
              {filtered.map(p => (
                <div key={p.id}
                  onClick={() => pickProperty(p, forTab)}
                  style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#e8e4dd', borderTop: '1px solid #1f1f1f', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontWeight: 500 }}>{p.address}</div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 1 }}>{[p.bedrooms && `${p.bedrooms}bd`, p.bathrooms && `${p.bathrooms}ba`, p.sqft && `${p.sqft} sqft`].filter(Boolean).join(" · ") || "No details"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Per-tab cached results
  const [listingResult, setListingResult] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [contractResult, setContractResult] = useState(null);
  const [inspectionResult, setInspectionResult] = useState(null);
  const [offerResult, setOfferResult] = useState(null);
  const [openHouseResult, setOpenHouseResult] = useState(null);
  const [sellerUpdateResult, setSellerUpdateResult] = useState(null);
  const [bioResult, setBioResult] = useState(null);
  const [bioTab, setBioTab] = useState("long");
  const [expiredResult, setExpiredResult] = useState(null);
  const [fsboResult, setFsboResult] = useState(null);
  const [cmaResult, setCmaResult] = useState(null);
  const [error, setError] = useState(null);

  // Output history (Supabase-backed)
  const [history, setHistory] = useState({});
  const [historyOpen, setHistoryOpen] = useState({});

  // Load outputs from Supabase for current tab.
  // Filters out sparse rows (no full_output) — those can exist if OpenAI failed mid-generation
  // or from legacy rows before the backend started populating content.
  const loadOutputs = useCallback(async (tabId) => {
    if (DEV_MODE) return;
    const token = await getToken();
    if (!token) return;
    const data = await supaFetch(`/user_outputs?user_id=eq.${userId}&tab=eq.${tabId}&full_output=not.is.null&order=created_at.desc&limit=20`, token);
    // On a fetch error supaFetch returns null (and has already logged it); keep whatever
    // history we had rather than blanking the panel. An empty array is a valid "no rows".
    if (Array.isArray(data)) {
      setHistory(prev => ({ ...prev, [tabId]: data.map(row => ({ id: row.id, data: row.full_output, preview: row.preview, time: new Date(row.created_at).getTime() })) }));
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load outputs when tab changes
  useEffect(() => { loadOutputs(tab); }, [tab, loadOutputs]);

  // Sync agent name to localStorage
  useEffect(() => {
    localStorage.setItem(AGENT_NAME_KEY, savedAgentName);
    setAgentName(savedAgentName);
  }, [savedAgentName]);

  // Sync sign-off to localStorage
  useEffect(() => {
    localStorage.setItem(AGENT_SIGNOFF_KEY, savedSignoff);
  }, [savedSignoff]);

  // Sync PDF branding to localStorage (logo can be large; swallow quota errors).
  useEffect(() => {
    try { localStorage.setItem(COMPANY_NAME_KEY, companyName); } catch {}
  }, [companyName]);
  useEffect(() => {
    try {
      if (logoData) localStorage.setItem(LOGO_KEY, logoData);
      else localStorage.removeItem(LOGO_KEY);
    } catch { toast.error("Couldn't save the logo on this device (storage full)."); }
  }, [logoData]);
  useEffect(() => {
    try {
      if (headshotData) localStorage.setItem(HEADSHOT_KEY, headshotData);
      else localStorage.removeItem(HEADSHOT_KEY);
    } catch { toast.error("Couldn't save the headshot on this device (storage full)."); }
  }, [headshotData]);

  // Shared image-upload pipeline: validate PNG/JPEG, downscale to <=maxW wide via canvas,
  // store as a PNG data URL. Used by the logo and the agent headshot.
  const readScaledImage = (file, maxW, setter, okMsg) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error("Image must be a PNG or JPEG."); return; }
    if (file.size > 1024 * 1024) { toast.error("That image is over 1MB — please use a smaller file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / (img.width || maxW));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        try {
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          setter(canvas.toDataURL("image/png"));
          toast.success(okMsg);
        } catch { toast.error("Couldn't process that image."); }
      };
      img.onerror = () => toast.error("Couldn't read that image.");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("Couldn't read that file.");
    reader.readAsDataURL(file);
  };
  const handleHeadshotFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    readScaledImage(file, 300, setHeadshotData, "Headshot saved.");
  };

  // Accept a PNG/JPEG logo, downscale it client-side to <=300px wide via canvas, and store the
  // result as a PNG data URL (keeps transparency for letterhead). Rejects >1MB originals.
  const handleLogoFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // let the user re-pick the same file later
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error("Logo must be a PNG or JPEG image."); return; }
    if (file.size > 1024 * 1024) { toast.error("That logo is over 1MB — please use a smaller file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 300;
        const scale = Math.min(1, maxW / (img.width || maxW));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        try {
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          setLogoData(canvas.toDataURL("image/png"));
          toast.success("Logo saved.");
        } catch { toast.error("Couldn't process that image."); }
      };
      img.onerror = () => toast.error("Couldn't read that image.");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  // Optional cover photo for the CMA report (session-only — too large to keep in localStorage).
  // Downscales to <=1200px wide and stores a JPEG data URL.
  const [cmaPhoto, setCmaPhoto] = useState("");
  const cmaPhotoInputRef = useRef(null);
  const handleCmaPhotoFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { toast.error("Photo must be a PNG, JPEG, or WebP image."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("That photo is over 10MB — please use a smaller file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1200;
        const scale = Math.min(1, maxW / (img.width || maxW));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        try {
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          setCmaPhoto(canvas.toDataURL("image/jpeg", 0.85));
          toast.success("Cover photo added.");
        } catch { toast.error("Couldn't process that image."); }
      };
      img.onerror = () => toast.error("Couldn't read that image.");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  // CMA "Save PDF": for a market-data CMA, fetch Street View photos + a pin map from the
  // backend (key stays server-side) and merge them into the structured payload, then render
  // the 6-page report. Image fetch failures degrade to placeholders — never block the export.
  const [cmaPdfLoading, setCmaPdfLoading] = useState(false);
  // Collapse RentCast's near-duplicate comps (e.g. "520 S Adria St" + "520-ST S Adria St" —
  // same property, two listing variants). Conservative: same property only when the rounded
  // coordinate matches (~11m) or, lacking coords, the address matches after stripping a
  // digit-attached suffix like "-ST". Keeps the first (RentCast orders most-similar first).
  const dedupeComps = (list) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const out = [];
    for (const c of list) {
      if (!c) continue;
      // Address is the reliable signal: strip a digit-attached suffix ("520-ST" -> "520")
      // then keep alphanumerics. A different unit/street number yields a different key, so
      // genuinely distinct comps (incl. same-building units, adjacent lots) are NOT collapsed.
      // Coordinates are only a fallback for address-less comps, at a tight ~0.1m epsilon —
      // never used to merge two addressed comps that merely round to the same ~11m cell.
      let key = String(c.address || "").toLowerCase()
        .replace(/(\d)\s*-\s*[a-z]{1,4}\b/g, "$1")
        .replace(/[^a-z0-9]/g, "");
      if (!key) {
        const lat = Number(c.latitude), lng = Number(c.longitude);
        key = (Number.isFinite(lat) && Number.isFinite(lng)) ? `geo:${lat.toFixed(6)},${lng.toFixed(6)}` : "";
      }
      if (!key) { out.push(c); continue; }
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  };
  const buildCmaPayload = (r, images) => ({
    mode: r.mode === "buyer" ? "buyer" : "seller",
    branding: { logo: logoData, company: companyName },
    agent: { name: savedAgentName, signoff: savedSignoff, headshot: headshotData },
    property: {
      address,
      // Form values win; fall back to RentCast's subject record so the spec grid + reference
      // banner are as complete as the comps (no "N/A" for data RentCast actually returns).
      beds: bedrooms || (r.subject_beds != null ? String(r.subject_beds) : ""),
      baths: bathrooms || (r.subject_baths != null ? String(r.subject_baths) : ""),
      sqft: sqft || (r.subject_sqft != null ? String(r.subject_sqft) : ""),
      lat: r.subject_lat, lng: r.subject_lng,
      lot: r.subject_lot, year: r.subject_year, type: r.subject_type,
      // True/false when market data ran; undefined for manual or pre-flag saved CMAs.
      subjectMatched: r.subject_matched,
      // User-uploaded cover photo wins; otherwise the subject Street View if we got one.
      photo: cmaPhoto || (images && images.subjectPhoto) || "",
    },
    mapImage: (images && images.mapImage) || "",
    valuation: {
      estimate: r.avm_estimate, rangeLow: r.avm_range_low, rangeHigh: r.avm_range_high,
      recommendedPrice: r.recommended_price, valueRange: r.value_range, askingPrice: price,
    },
    comps: (r.market_data && Array.isArray(r.comparables) ? dedupeComps(r.comparables) : []).map((c, i) => ({
      ...c,
      photo: (images && Array.isArray(images.comps) && images.comps[i] && images.comps[i].photo) || "",
    })),
    narrative: r.summary,
  });
  const handleCmaSavePdf = async () => {
    if (cmaPdfLoading) return;
    const r = cmaResult;
    if (!r) return;
    const hasCoords = r.market_data && (r.subject_lat != null ||
      (Array.isArray(r.comparables) && r.comparables.some(c => c && c.latitude != null)));
    if (!hasCoords) { exportCmaReport(buildCmaPayload(r, null)); return; }
    setCmaPdfLoading(true);
    let images = null;
    try {
      const token = await getToken();
      if (token) {
        const res = await fetch(`${API_URL}/cma-report-images`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            user_id: userId,
            subject: { lat: r.subject_lat, lng: r.subject_lng },
            comps: dedupeComps(r.comparables || []).slice(0, 8).map(c => ({ lat: c.latitude, lng: c.longitude })),
          }),
        });
        if (res.ok) images = await res.json();
      }
    } catch (e) {
      console.warn("cma-report-images failed:", e);
    } finally {
      setCmaPdfLoading(false);
    }
    exportCmaReport(buildCmaPayload(r, images));
  };

  const addToHistory = useCallback(async (tabId, data) => {
    // Prod: the backend populated the row's preview + full_output in the generation endpoint.
    // Just refresh history from the server so the new row shows up immediately.
    if (!DEV_MODE) {
      loadOutputs(tabId);
      return;
    }
    // Dev mode fallback — no backend, keep local in-memory history with a best-effort preview.
    let preview = "";
    if (tabId === "listing" && data?.mls) preview = data.mls.slice(0, 80);
    else if (tabId === "followup" && Array.isArray(data)) preview = data[0]?.subject?.slice(0, 80) || "Emails";
    else if (tabId === "contract" && Array.isArray(data)) preview = data[0]?.label?.slice(0, 80) || "Contract";
    else if (tabId === "inspection" && Array.isArray(data?.items)) preview = data.items[0]?.label?.slice(0, 80) || "Inspection";
    else if (data?.subject) preview = data.subject.slice(0, 80);
    else if (tabId === "bio" && data?.short) preview = data.short.slice(0, 80);
    else if (tabId === "cma" && data?.summary) preview = data.summary.slice(0, 80);
    else if (tabId === "justsold" && data?.social) preview = data.social.slice(0, 80);
    else preview = tabId;
    const entry = { data, preview, time: Date.now() };
    setHistory(prev => ({ ...prev, [tabId]: [entry, ...(prev[tabId] || [])].slice(0, 20) }));
  }, [loadOutputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteHistoryItem = async (tabId, idx) => {
    const items = history[tabId] || [];
    const item = items[idx];
    if (item?.id && !DEV_MODE) {
      const token = await getToken();
      if (token) {
        // Null out the content instead of deleting — the row doubles as the usage-count
        // record for plan-limit enforcement, so hard-deleting it would refund a generation.
        // loadOutputs filters `full_output=not.is.null`, so nulled rows disappear from history.
        await supaFetch(`/user_outputs?id=eq.${item.id}`, token, {
          method: "PATCH",
          body: JSON.stringify({ preview: null, full_output: null }),
        });
        loadOutputs(tabId);
        return;
      }
    }
    setHistory(prev => {
      const tabHistory = [...(prev[tabId] || [])];
      tabHistory.splice(idx, 1);
      return { ...prev, [tabId]: tabHistory };
    });
  };

  const restoreHistoryItem = (tabId, item) => {
    if (tabId === "listing") { setListingResult(item.data); setOutTab("mls"); }
    else if (tabId === "followup") { setEmailResult(item.data); }
    else if (tabId === "contract") { setContractResult(item.data); }
    else if (tabId === "inspection") { setInspectionResult(item.data); }
    else if (tabId === "offer") { setOfferResult(item.data); }
    else if (tabId === "openhouse") { setOpenHouseResult(item.data); }
    else if (tabId === "sellerupdate") { setSellerUpdateResult(item.data); }
    else if (tabId === "bio") { setBioResult(item.data); setBioTab("long"); }
    else if (tabId === "expired") { setExpiredResult(item.data); }
    else if (tabId === "fsbo") { setFsboResult(item.data); }
    else if (tabId === "cma") { setCmaResult(item.data); }
    else if (tabId === "justsold") { setJustSoldResult(item.data); setJsOutTab("social"); }
  };

  const updateProperty = async () => {
    setSharedProperty({ address, bedrooms, bathrooms, halfBaths, sqft, price, features });
    setPropertySaved(true);
    setTimeout(() => setPropertySaved(false), 1500);
    if (!DEV_MODE) {
      const token = await getToken();
      if (token) {
        const rows = await supaFetch("/user_properties", token, {
          method: "POST",
          headers: { Prefer: "resolution=merge-duplicates,return=representation" },
          body: JSON.stringify({
            user_id: userId, address, bedrooms, bathrooms, half_baths: halfBaths,
            sqft, price, features,
          }),
        });
        // Keep the in-memory copy current so the duplicate-lookup guard sees this save.
        if (Array.isArray(rows) && rows[0]) {
          setSupaProps(prev => {
            const idx = prev.findIndex(p => p.id === rows[0].id);
            return idx >= 0 ? prev.map((p, i) => (i === idx ? rows[0] : p)) : [...prev, rows[0]];
          });
        }
      }
    }
  };

  const applyCustomTone = () => {
    if (!customToneInput.trim()) return;
    setTone("custom");
    setActiveToneLabel(customToneInput.trim());
    setCustomToneOpen(false);
  };

  const clearCustomTone = () => {
    setTone("Luxury"); setActiveToneLabel("Luxury");
    setCustomToneInput(""); setCustomToneOpen(false);
  };

  // Smart validation
  const canGenerate = () => {
    if (!isUnlimited && usesRemaining <= 0) return false;
    if (tab === "listing") return address.trim().length > 0;
    if (tab === "followup") return leadName.trim().length > 0 && sharedProperty.address.trim().length > 0;
    if (tab === "contract") return contractText.trim().length > 0;
    if (tab === "inspection") return reportText.trim().length > 0;
    if (tab === "openhouse") return ohVisitorName.trim().length > 0;
    if (tab === "sellerupdate") return sellerName.trim().length > 0;
    if (tab === "bio") return savedAgentName.trim().length > 0 && bioMarket.trim().length > 0;
    if (tab === "expired") return expiredName.trim().length > 0;
    if (tab === "fsbo") return fsboName.trim().length > 0;
    if (tab === "offer") return buyerName.trim().length > 0 && sharedProperty.address.trim().length > 0 && offerPrice.trim().length > 0;
    if (tab === "cma") return address.trim().length > 0;
    return false;
  };

  // Keyboard shortcut: Ctrl/Cmd+Enter to generate from anywhere in the app
  // (except when focused in a non-input control that handles Enter itself).
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.key === "Enter" && (e.metaKey || e.ctrlKey))) return;
      // Don't fire if the user's cursor is in Textarea-like fields where they may be inserting newlines,
      // but the shortcut is specifically Cmd/Ctrl+Enter which is universally "submit" so we allow it.
      e.preventDefault();
      if (!loading) run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loading, userId, address, bedrooms, bathrooms, halfBaths, sqft, price, features, activeToneLabel, leadName, agentName, sharedProperty, stage, leadContext, contractText, reportText, buyerName, offerPrice, earnestMoney, financing, contingencies, closeDate, offerState, offerNotes]);

  // Fair Housing compliance scan — runs after each user-facing generation (regex/word-list,
  // no LLM). Non-blocking: a failure never affects the generation itself.
  const [complianceFlags, setComplianceFlags] = useState([]);
  const complianceTextFor = (t, d) => {
    if (!d || t === "contract" || t === "inspection" || t === "cma") return ""; // contract/inspection = factual summaries; CMA off-limits
    if (t === "followup") return (d.emails || []).map((e) => e && e.body).filter(Boolean).join("\n");
    if (Array.isArray(d)) return d.map((x) => (x && x.body) || "").join("\n");
    return Object.values(d).filter((v) => typeof v === "string").join("\n");
  };
  const scanCompliance = async (text) => {
    if (!text || !text.trim()) { setComplianceFlags([]); return; }
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/fair-housing-scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userId, text }),
      });
      if (r.ok) { const j = await r.json(); setComplianceFlags(Array.isArray(j.flags) ? j.flags : []); }
    } catch { /* non-blocking — the compliance scan never breaks a generation */ }
  };

  // MLS character counter + trim-to-fit (Listing output only)
  const [mlsBoard, setMlsBoard] = useState(MLS_LIMITS[0].name);
  const [trimming, setTrimming] = useState(false);
  const mlsLimit = (MLS_LIMITS.find((b) => b.name === mlsBoard) || MLS_LIMITS[0]).limit;
  const trimListing = async () => {
    if (!listingResult || !listingResult.mls) return;
    setTrimming(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/trim-listing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userId, text: listingResult.mls, limit: mlsLimit }),
      });
      if (r.ok) {
        const j = await r.json();
        if (j.text) { setListingResult((prev) => (prev ? { ...prev, mls: j.text } : prev)); scanCompliance(j.text); }
      }
    } catch { /* ignore */ } finally { setTrimming(false); }
  };

  const run = async () => {
    if (!canGenerate()) { setTriedSubmit(true); return; }
    if (isDemoMode && demoCapReached) {
      setError(`You've used all ${DEMO_GEN_CAP} demo generations. Sign up to continue.`);
      return;
    }
    setTriedSubmit(false);
    setLoading(true);
    setError(null);
    setComplianceFlags([]);
    try {
      let url, body;
      if (tab === "listing") {
        url = `${API_URL}/generate`;
        body = { user_id: userId, address, bedrooms, bathrooms, half_baths: halfBaths, sqft, price, features, tone: activeToneLabel };
      } else if (tab === "followup") {
        url = `${API_URL}/generate-followup`;
        body = { user_id: userId, lead_name: leadName, agent_name: agentName, property: sharedProperty.address, buyer_stage: stage, context: leadContext, signoff: savedSignoff };
      } else if (tab === "contract") {
        url = `${API_URL}/generate-contract`;
        body = { user_id: userId, contract_text: contractText };
      } else if (tab === "inspection") {
        url = `${API_URL}/generate-inspection`;
        body = { user_id: userId, report_text: reportText };
      } else if (tab === "openhouse") {
        url = `${API_URL}/generate-openhouse`;
        body = { user_id: userId, property: sharedProperty.address, date: ohDate, visitor_name: ohVisitorName, notes: ohNotes, agent_name: savedAgentName, signoff: savedSignoff };
      } else if (tab === "sellerupdate") {
        url = `${API_URL}/generate-seller-update`;
        body = { user_id: userId, seller_name: sellerName, property: sharedProperty.address, days_on_market: daysOnMarket, showings_this_week: showingsThisWeek, feedback: showingFeedback, price_reduction: priceReduction, agent_name: savedAgentName, signoff: savedSignoff };
      } else if (tab === "bio") {
        url = `${API_URL}/generate-bio`;
        body = { user_id: userId, agent_name: savedAgentName, years_experience: bioYears, market: bioMarket, specialties: bioSpecialties.join(", "), personal_fact: bioFact, tone: bioTone };
      } else if (tab === "expired") {
        url = `${API_URL}/generate-expired`;
        body = { user_id: userId, homeowner_name: expiredName, property: sharedProperty.address, original_price: expiredPrice, days_on_market: expiredDays, agent_name: savedAgentName, signoff: savedSignoff };
      } else if (tab === "fsbo") {
        url = `${API_URL}/generate-fsbo`;
        body = { user_id: userId, homeowner_name: fsboName, property: sharedProperty.address, asking_price: fsboPrice, agent_name: savedAgentName, context: fsboContext, signoff: savedSignoff };
      } else if (tab === "offer") {
        url = `${API_URL}/generate-offer-summary`;
        body = { user_id: userId, buyer_name: buyerName, property: sharedProperty.address, offer_price: offerPrice, earnest_money: earnestMoney, financing, contingencies, close_date: closeDate, notes: offerNotes, agent_name: savedAgentName, signoff: savedSignoff };
      } else if (tab === "cma") {
        url = `${API_URL}/generate-cma`;
        body = { user_id: userId, address, bedrooms, bathrooms, sqft, price, features, market: cmaMarket, use_market_data: cmaHasLookups && useMarketData, mode: cmaMode };
      } else { setLoading(false); return; }

      const reqHeaders = { "Content-Type": "application/json" };
      // Every generation endpoint now JWT-derives user_id server-side (anti-impersonation), so attach
      // the Supabase session JWT for real users. Demo users have no session -> token is null -> the
      // header is omitted and the backend takes the demo path off the body's "demo-" user_id.
      const token = await getToken();
      if (token) reqHeaders["Authorization"] = `Bearer ${token}`;
      const res = await fetch(url, {
        method: "POST",
        headers: reqHeaders,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `Server error: ${res.status}`;
        try { const j = await res.json(); if (j && j.detail) msg = j.detail; } catch (_) {}
        // Backend caps demo generations per IP per day; a 429 here means we've hit that cap.
        // Lock the demo UI so subsequent clicks fall back to the sessionStorage-gated
        // "Sign up to continue" path instead of firing more doomed requests.
        if (res.status === 429 && isDemoMode) {
          setDemoGenUsed(DEMO_GEN_CAP);
          try { sessionStorage.setItem("demo_generations_used", String(DEMO_GEN_CAP)); } catch {}
        }
        throw new Error(msg);
      }
      const data = await res.json();

      // For listing tab, also fetch neighborhood in parallel
      if (tab === "listing") {
        setListingResult(data); setOutTab("mls"); addToHistory("listing", data);
        // Fire neighborhood generation in background
        fetch(`${API_URL}/generate-neighborhood`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ user_id: userId, address, features }),
        }).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))).then(nd => {
          setListingResult(prev => prev ? { ...prev, neighborhood: nd.description } : prev);
        }).catch(err => { console.warn("Neighborhood fetch failed:", err); });
      }
      else if (tab === "followup") { setEmailResult(data.emails); addToHistory("followup", data.emails); }
      else if (tab === "contract") { setContractResult(data.items); addToHistory("contract", data.items); }
      else if (tab === "inspection") { setInspectionResult(data); addToHistory("inspection", data); }
      else if (tab === "openhouse") { setOpenHouseResult(data); addToHistory("openhouse", data); }
      else if (tab === "sellerupdate") { setSellerUpdateResult(data); addToHistory("sellerupdate", data); }
      else if (tab === "bio") { setBioResult(data); setBioTab("long"); addToHistory("bio", data); }
      else if (tab === "expired") { setExpiredResult(data); addToHistory("expired", data); }
      else if (tab === "fsbo") { setFsboResult(data); addToHistory("fsbo", data); }
      else if (tab === "offer") { setOfferResult(data); addToHistory("offer", data); }
      else if (tab === "cma") { setCmaResult(data); addToHistory("cma", data); if (data.lookups_used != null) setPropLookupsUsed(data.lookups_used); if (data.lookups_limit != null) setPropLookupsLimit(data.lookups_limit); }

      // Fair Housing compliance scan on the generated copy (non-blocking).
      scanCompliance(complianceTextFor(tab, data));

      // Log generation to backend (counts toward plan limit)
      logGeneration(tab);
      if (!isDemoMode) {
        track("generation_created", { tool: tab, plan: subscription?.plan });
        if (tab === "cma") track("cma_generated", { mode: cmaMode, market_data: !!data.market_data });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    const str = String(text ?? "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(str);
      } else {
        // Fallback for non-secure contexts or older browsers
        const ta = document.createElement("textarea");
        ta.value = str;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    } catch (e) {
      console.warn("copy failed:", e);
      toast.error("Couldn't copy — select the text and press Ctrl+C");
    }
  };

  // Web Share — mobile-native "send this output to Messages/WhatsApp/Mail/…". Shown only on
  // mobile viewports where the browser supports it, so desktop stays pixel-identical (some
  // desktop browsers do expose navigator.share). Cancelling the sheet rejects with
  // AbortError, which we swallow.
  const canWebShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const ShareBtn = ({ text, title = "Pitcherly", style }) => {
    if (!canWebShare || !isMobile) return null;
    const t = String(text ?? "");
    if (!t.trim()) return null;
    return (
      <button
        className="act-btn"
        style={style}
        onClick={() => { navigator.share({ title, text: t }).catch(() => {}); }}
      >
        Share
      </button>
    );
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Money formatters for Property Info (raw RentCast number -> "$101,602" / "$892k").
  const fmtMoney = (v) => {
    if (v == null || String(v).trim() === "") return "";
    const n = Number(String(v).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : String(v);
  };
  // CMA recommended offer/price: format a bare number ("920000") or bare numeric range as full
  // USD, but leave an already-formatted string or one with reasoning UNTOUCHED (so the model's
  // wording and seller's already-correct output are never mangled). Mirrors export.js fmtRec.
  const fmtRec = (v) => {
    if (v == null) return "";
    const s = String(v).trim();
    if (!s) return "";
    const one = (tok) => {
      const d = tok.replace(/[^0-9.]/g, "");
      const n = Number(d);
      return d && d !== "." && Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : tok;
    };
    if (/^\$?\d[\d,]*(\.\d+)?$/.test(s)) return one(s);
    const m = s.match(/^\$?\s*(\d[\d,]*(?:\.\d+)?)\s*[-–—]\s*\$?\s*(\d[\d,]*(?:\.\d+)?)\s*$/);
    if (m) return `${one(m[1])}–${one(m[2])}`;
    // Leading bare full-dollar figure (4+ digits, not already comma-grouped) followed by prose,
    // e.g. "920000 — ~1% below list": format that figure, leave the prose. Already-grouped strings
    // ("$915,000 — …") have ≤3 leading digits before a comma, so \d{4,} never matches them.
    return s.replace(/^(\$?)(\d{4,})(?![\d,])/, (_m, _d, num) => `$${Number(num).toLocaleString()}`);
  };
  const fmtK = (v) => {
    const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(n) || n === 0) return "";
    return n >= 1000 ? `$${Math.round(n / 1000).toLocaleString()}k` : `$${Math.round(n).toLocaleString()}`;
  };
  // Format an ISO date for display ("2009-08-20T00:00:00.000Z" -> "Aug 20, 2009"). Uses UTC so a
  // midnight-UTC date doesn't shift a day back in western timezones. Leaves non-ISO/already-
  // friendly strings as-is; blank for missing/invalid (never "Invalid Date").
  const fmtDate = (v) => {
    if (!v) return "";
    const s = String(v);
    if (!s.includes("T")) return s;
    const d = new Date(s);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
  };
  // Format a real moment-in-time stamp (lastLookedUpAt / createdAt) in the user's LOCAL zone —
  // unlike fmtDate, which forces UTC for date-only RentCast values. Using UTC here would show an
  // evening-US agent tomorrow's date on a lookup they just did.
  const fmtStamp = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // Full CMA report text (summary + numbers + comps + factors) for copy / PDF export.
  const cmaExportText = (r) => {
    let t = `${r.summary || ""}\n\nValue Range: ${r.value_range || ""}\nRecommended ${r.mode === "buyer" ? "Offer" : "Price"}: ${fmtRec(r.recommended_price)}`;
    if (r.market_data && Array.isArray(r.comparables) && r.comparables.length) {
      t += "\n\nComparable Sales:\n" + r.comparables.map(c =>
        `• ${c.address || "—"} — ${c.price != null ? "$" + Number(c.price).toLocaleString() : "—"} (${c.bedrooms ?? "?"}bd/${c.bathrooms ?? "?"}ba, ${c.sqft ? Number(c.sqft).toLocaleString() + " sqft" : "? sqft"}${c.distance != null ? ", " + c.distance + " mi" : ""})`
      ).join("\n");
    }
    if (r.key_factors && r.key_factors.length) {
      t += "\n\nKey Factors:\n" + r.key_factors.map(f => `• ${f}`).join("\n");
    }
    return t;
  };

  const currentResult = tab === "listing" ? listingResult : tab === "followup" ? emailResult : tab === "contract" ? contractResult : tab === "inspection" ? inspectionResult : tab === "openhouse" ? (openHouseResult || justSoldResult) : tab === "sellerupdate" ? sellerUpdateResult : tab === "bio" ? bioResult : tab === "expired" ? expiredResult : tab === "fsbo" ? fsboResult : tab === "offer" ? offerResult : tab === "cma" ? cmaResult : null;

  // Just Sold generation (separate from main run)
  const runJustSold = async () => {
    if (!jsSalePrice.trim()) return;
    setJsLoading(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/generate-justsold`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userId, property: sharedProperty.address, sale_price: jsSalePrice, days_on_market: jsDaysOnMarket, fun_fact: jsFunFact, agent_name: savedAgentName, signoff: savedSignoff }),
      });
      if (!res.ok) {
        let msg = `Server error: ${res.status}`;
        try { const j = await res.json(); if (j && j.detail) msg = j.detail; } catch (_) {}
        if (res.status === 429 && isDemoMode) {
          setDemoGenUsed(DEMO_GEN_CAP);
          try { sessionStorage.setItem("demo_generations_used", String(DEMO_GEN_CAP)); } catch {}
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setJustSoldResult(data); setJsOutTab("social");
      addToHistory("justsold", data);
      logGeneration("justsold");
      if (!isDemoMode) track("generation_created", { tool: "justsold", plan: subscription?.plan });
    } catch (err) { setError(err.message); }
    finally { setJsLoading(false); }
  };
  const tabHistory = history[tab] || [];
  const isReady = canGenerate();

  // Render MLS paragraphs with dividers
  const renderMlsText = (text) => {
    if (!text) return null;
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
    if (paragraphs.length <= 1) return <OutText>{text}</OutText>;
    return (
      <div className="mls-paragraphs">
        {paragraphs.map((p, i) => <OutText key={i} className="mls-para">{p}</OutText>)}
      </div>
    );
  };

  // History section component
  const renderHistory = () => {
    if (tabHistory.length === 0) return null;
    const isOpen = historyOpen[tab];
    return (
      <div className="history-section">
        <button className="history-toggle" onClick={() => setHistoryOpen(p => ({ ...p, [tab]: !p[tab] }))}>
          <span className={`history-arrow ${isOpen ? "open" : ""}`}>▶</span>
          Recent Outputs ({tabHistory.length})
        </button>
        {isOpen && (
          <div className="history-list">
            {tabHistory.map((item, i) => (
              <div key={i} className="history-item" onClick={() => restoreHistoryItem(tab, item)}>
                <div className="history-preview">{item.preview}</div>
                <div className="history-time">{formatTime(item.time)}</div>
                <button aria-label="Delete history item" title="Delete" className="history-delete" onClick={e => { e.stopPropagation(); deleteHistoryItem(tab, i); }}>🗑</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const BIO_SPECIALTIES = ["Luxury", "First-Time Buyers", "Investment", "Relocation", "Commercial", "New Construction"];
  const BIO_TONES = ["Professional", "Warm", "Bold"];

  const toggleBioSpecialty = (s) => {
    setBioSpecialties(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const nextVisitor = () => { setOhVisitorName(""); setOhNotes(""); setOpenHouseResult(null); };

  // ─── PROPERTY INFO TAB ───
  const PROPS_KEY = "pitcherly_properties";
  const loadProperties = () => safeParse(PROPS_KEY, []);
  const saveProperties = (list) => safeSet(PROPS_KEY, list);

  const [piAddress, setPiAddress] = useState("");
  const [piLoading, setPiLoading] = useState(false);
  const [piData, setPiData] = useState(null);
  const [piSaved, setPiSaved] = useState(false);
  const [piProperties, setPiProperties] = useState(() => loadProperties());
  const [piNotFound, setPiNotFound] = useState(false);

  // Property lookup counter (RentCast tiered limits)
  const [propLookupsUsed, setPropLookupsUsed] = useState(0);
  // Authoritative limit comes from the backend (loaded below); this is just the initial fallback.
  const [propLookupsLimit, setPropLookupsLimit] = useState(
    isTrial ? 0 : subscription?.plan === "pro" ? 50 : subscription?.plan === "starter" ? 10 : 0
  );

  // Load real lookup usage (count + limit) from the backend on mount and whenever the plan
  // resolves, so the counter survives a page refresh instead of resetting to 0. Single source
  // of truth for both the Property Info counter and the CMA "Use market data" counter.
  useEffect(() => {
    if (!userId || isDemoMode) return;
    (async () => {
      const token = await getToken();
      fetch(`${API_URL}/get-property-lookups?user_id=${encodeURIComponent(userId)}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        .then(r => r.json())
        .then(data => {
          if (data?.count != null) setPropLookupsUsed(data.count);
          if (data?.limit != null) setPropLookupsLimit(data.limit);
        })
        .catch(e => console.warn("lookup-usage fetch failed:", e));
    })();
  }, [userId, isDemoMode, subscription?.plan, subscription?.stripe_status]);

  // ─── Duplicate-lookup guard ───
  // {local, supa, savedDate} while the "already looked up" confirm dialog is open.
  const [dupGuard, setDupGuard] = useState(null);

  // Match an address against BOTH stores (Property Info localStorage + Supabase
  // user_properties) using the shared normalizer — formatting variants like
  // "Ave" vs "Avenue" or a missing city/zip still match.
  const findSavedMatches = (addr) => {
    const key = addressMatchKey(addr);
    if (!key) return { local: null, supa: null };
    return {
      local: loadProperties().find(p => addressMatchKey(p.address) === key) || null,
      supa: supaProps.find(p => addressMatchKey(p.address) === key) || null,
    };
  };

  // Entry point for the Look Up button: if the address is already saved, ask before
  // burning a lookup. No match -> straight to the live lookup, zero extra friction.
  const requestPropertyLookup = () => {
    const addr = piAddress.trim();
    if (!addr || isTrial) return;
    const { local, supa } = findSavedMatches(addr);
    if (local || supa) {
      const savedDate = local
        ? (local.lastLookedUpAt || local.createdAt)
        : (supa.last_looked_up_at || supa.updated_at || supa.created_at);
      setDupGuard({ local, supa, savedDate: savedDate || null });
      return;
    }
    runPropertyLookup();
  };

  // "Load saved (free)" — populate the form from the saved record. No API call, no lookup burned.
  const loadSavedFromGuard = () => {
    const g = dupGuard;
    setDupGuard(null);
    if (!g) return;
    if (g.local) { loadPiProperty(g.local); return; }
    // Supabase-only match: the listing-form store carries a smaller field set.
    const p = g.supa;
    setPiData({
      address: p.address || "", source: "manual",
      sqft: p.sqft != null ? String(p.sqft) : "",
      bedrooms: p.bedrooms != null ? String(p.bedrooms) : "",
      bathrooms: p.bathrooms != null ? String(p.bathrooms) : "",
      halfBaths: p.half_baths != null ? String(p.half_baths) : "",
      lotSize: "", yearBuilt: "", propertyType: "", lastSaleDate: "", lastSalePrice: "",
      assessedValue: "", taxAmount: "", neighborhood: "",
      notes: p.features ? `Features: ${p.features}` : "",
    });
    setPiAddress(p.address || "");
    setPiNotFound(false); setPiValidationFailed(false); setPiSaved(false);
  };

  // Merge a fresh lookup into an existing saved property. RentCast-sourced fields win,
  // but a null/blank from RentCast never wipes a non-empty saved value, and user-entered
  // fields (notes, address as the user saved it) always survive. Same id — never a duplicate.
  const mergeLookupIntoSaved = (saved, fresh) => {
    const pick = (newVal, oldVal) =>
      newVal != null && String(newVal).trim() !== "" ? newVal : (oldVal || "");
    return {
      ...saved,
      source: fresh.source === "rentcast" ? "rentcast" : saved.source || "manual",
      sqft: pick(fresh.sqft, saved.sqft),
      bedrooms: pick(fresh.bedrooms, saved.bedrooms),
      bathrooms: pick(fresh.bathrooms, saved.bathrooms),
      halfBaths: pick(fresh.halfBaths, saved.halfBaths),
      lotSize: pick(fresh.lotSize, saved.lotSize),
      yearBuilt: pick(fresh.yearBuilt, saved.yearBuilt),
      propertyType: pick(fresh.propertyType, saved.propertyType),
      lastSaleDate: pick(fresh.lastSaleDate, saved.lastSaleDate),
      lastSalePrice: pick(fresh.lastSalePrice, saved.lastSalePrice),
      assessedValue: pick(fresh.assessedValue, saved.assessedValue),
      taxAmount: pick(fresh.taxAmount, saved.taxAmount),
      neighborhood: pick(fresh.neighborhood, saved.neighborhood),
      market_value: fresh.market_value || saved.market_value || null,
      notes: saved.notes || "",
      lastLookedUpAt: new Date().toISOString(),
    };
  };

  // Refresh the matched Supabase user_properties row in place (PATCH by id — never a new
  // row). Only RentCast fields the row actually stores; price/features/address are
  // user-entered and untouched. The freshness stamp column is detected from the row itself
  // so a missing column can't 42703-reject the whole PATCH (see the stripe_status gotcha).
  const updateSupaPropertyFromLookup = async (row, raw) => {
    try {
      const token = await getToken();
      if (!token) return;
      const patch = {};
      const put = (col, v) => { if (v != null && String(v).trim() !== "") patch[col] = String(v); };
      put("bedrooms", raw.bedrooms);
      put("bathrooms", raw.bathrooms);
      put("half_baths", raw.halfBaths);
      put("sqft", raw.sqft);
      if ("last_looked_up_at" in row) patch.last_looked_up_at = new Date().toISOString();
      else if ("updated_at" in row) patch.updated_at = new Date().toISOString();
      if (Object.keys(patch).length === 0) return;
      const res = await supaFetch(`/user_properties?id=eq.${row.id}`, token, {
        method: "PATCH",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(patch),
      });
      if (Array.isArray(res) && res[0]) {
        setSupaProps(prev => prev.map(p => (p.id === res[0].id ? res[0] : p)));
      } else {
        // Don't fail the lookup over this, but never let the write fail silently.
        console.warn("user_properties refresh write failed:", res);
      }
    } catch (e) {
      console.warn("user_properties refresh write failed:", e);
    }
  };

  const runPropertyLookup = async () => {
    const addr = piAddress.trim();
    if (!addr || isTrial) return;
    setPiLoading(true); setPiNotFound(false); setPiSaved(false);
    // plan is now derived server-side from Supabase; the field is sent for
    // backward compatibility but ignored by the backend.
    const effectiveStatus = subscription?.stripe_status;
    const plan = subscription?.plan === "pro" && effectiveStatus === "active" ? "pro" : "starter";
    try {
      const token = await getToken();
      if (!token) { setPiLoading(false); setPiNotFound(true); return; }
      const res = await fetch(`${API_URL}/property-lookup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ address: addr, plan, user_id: userId }),
      });
      const raw = await res.json();
      if (raw.used != null) setPropLookupsUsed(raw.used);
      if (raw.limit != null) setPropLookupsLimit(raw.limit);
      if (raw.found) {
        const fresh = {
          address: addr,
          source: raw.source || "manual",
          sqft: raw.sqft != null ? String(raw.sqft) : "",
          bedrooms: raw.bedrooms != null ? String(raw.bedrooms) : "",
          bathrooms: raw.bathrooms != null ? String(raw.bathrooms) : "",
          halfBaths: raw.halfBaths != null ? String(raw.halfBaths) : "",
          lotSize: raw.lotSize != null ? String(raw.lotSize) : "",
          yearBuilt: raw.yearBuilt != null ? String(raw.yearBuilt) : "",
          propertyType: raw.propertyType != null ? String(raw.propertyType) : "",
          lastSaleDate: fmtDate(raw.lastSaleDate),
          lastSalePrice: fmtMoney(raw.lastSalePrice),
          assessedValue: fmtMoney(raw.assessedValue),
          taxAmount: fmtMoney(raw.taxAmount),
          neighborhood: raw.neighborhood != null ? String(raw.neighborhood) : "",
          market_value: raw.market_value && raw.market_value.estimate != null ? raw.market_value : null,
          notes: "",
          lastLookedUpAt: new Date().toISOString(),
        };
        // Re-lookup of an already-saved address updates the existing record in place
        // (both stores) — never a second entry for the same property.
        const { local, supa } = findSavedMatches(addr);
        let next = fresh;
        if (local) {
          next = mergeLookupIntoSaved(local, fresh);
          const list = loadProperties();
          const idx = list.findIndex(p => p.id === local.id);
          if (idx >= 0) {
            list[idx] = next;
            saveProperties(list);
            setPiProperties(list);
          }
        }
        if (supa?.id) updateSupaPropertyFromLookup(supa, raw);
        setPiData(next);
        setPiNotFound(false);
      } else {
        setPiData({ address: addr, source: "manual", sqft: "", bedrooms: "", bathrooms: "", halfBaths: "", lotSize: "", yearBuilt: "", propertyType: "", lastSaleDate: "", lastSalePrice: "", assessedValue: "", taxAmount: "", neighborhood: "", notes: "" });
        setPiNotFound(true);
      }
      logGeneration("propinfo");
      if (!isDemoMode) track("property_lookup", { plan: subscription?.plan });
    } catch (err) {
      console.error("Property lookup error:", err);
      setPiData({ address: addr, source: "manual", sqft: "", bedrooms: "", bathrooms: "", halfBaths: "", lotSize: "", yearBuilt: "", propertyType: "", lastSaleDate: "", lastSalePrice: "", assessedValue: "", taxAmount: "", neighborhood: "", notes: "" });
      setPiNotFound(true);
    } finally {
      setPiLoading(false);
    }
  };

  const [piValidationFailed, setPiValidationFailed] = useState(false);
  const savePiProperty = (overrideData) => {
    const data = overrideData || piData;
    const addr = data?.address || piAddress.trim();
    if (!data || !addr || !data.propertyType?.trim() || !data.sqft?.trim() || !data.bedrooms?.trim() || !data.bathrooms?.trim()) {
      setPiValidationFailed(true);
      return;
    }
    setPiValidationFailed(false);
    const saveData = { ...data, address: addr };
    const existing = loadProperties();
    // Match by id when editing a saved record; otherwise exact address. (The lookup/guard
    // paths carry the matched id forward, so the in-place merge handles formatting-variant
    // dedup there — keeping this branch exact-match avoids a fresh manual save silently
    // replacing a different saved record and dropping its notes.)
    const idx = saveData.id ? existing.findIndex(p => p.id === saveData.id) : existing.findIndex(p => p.address === saveData.address);
    const entry = { ...saveData, id: idx >= 0 ? existing[idx].id : genId(), createdAt: idx >= 0 ? existing[idx].createdAt : new Date().toISOString() };
    if (idx >= 0) existing[idx] = entry; else existing.unshift(entry);
    saveProperties(existing);
    setPiProperties(existing);
    setPiData(entry);
    setPiSaved(true);
    setTimeout(() => setPiSaved(false), 2000);
  };

  const deletePiProperty = (id) => {
    const updated = loadProperties().filter(p => p.id !== id);
    saveProperties(updated);
    setPiProperties(updated);
    if (piData?.id === id) { setPiData(null); setPiAddress(""); setPiValidationFailed(false); }
  };

  const loadPiProperty = (prop) => {
    setPiData({
      id: prop.id, address: prop.address || "", source: prop.source || "manual",
      sqft: prop.sqft || "", bedrooms: prop.bedrooms || "", bathrooms: prop.bathrooms || "", halfBaths: prop.halfBaths || "",
      lotSize: prop.lotSize || "", yearBuilt: prop.yearBuilt || "", propertyType: prop.propertyType || "",
      lastSaleDate: fmtDate(prop.lastSaleDate), lastSalePrice: prop.lastSalePrice || "",
      assessedValue: prop.assessedValue || "", taxAmount: prop.taxAmount || "",
      neighborhood: prop.neighborhood || "", notes: prop.notes || "",
      market_value: prop.market_value || null,
      lastLookedUpAt: prop.lastLookedUpAt || null,
    });
    setPiAddress(prop.address || "");
    setPiNotFound(false);
    setPiValidationFailed(false);
    setPiSaved(false);
  };

  const [piSearch, setPiSearch] = useState("");

  return (
    <>
      <style>{styles}</style>
      {whatsNewOpen && !isDemoMode && (
        <Suspense fallback={null}><WhatsNewModal onClose={dismissWhatsNew} /></Suspense>
      )}
      {batchEmail && !isDemoMode && (
        <Suspense fallback={null}>
          <BatchSendModal
            subject={batchEmail.subject}
            body={batchEmail.body}
            userEmail={userEmail}
            onClose={() => setBatchEmail(null)}
          />
        </Suspense>
      )}
      {dupGuard && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setDupGuard(null)}
        >
          <div
            style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12, width: "min(440px, 100%)", fontFamily: "'Geist', sans-serif", color: "#e8e4dd", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: "1px solid #2a2a2a" }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>You've already looked up this property</div>
              <button aria-label="Close" onClick={() => setDupGuard(null)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
              {(dupGuard.local?.address || dupGuard.supa?.address) && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#e8e4dd", background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px" }}>
                  {dupGuard.local?.address || dupGuard.supa?.address}
                </div>
              )}
              <p style={{ margin: 0, fontSize: 13, color: "#c9c3ba", lineHeight: 1.6 }}>
                {dupGuard.savedDate && fmtStamp(dupGuard.savedDate) ? `Saved on ${fmtStamp(dupGuard.savedDate)}. ` : "Already saved. "}
                You can load your saved copy for free, or refresh it with the latest data. If this isn't the property you meant, close this and check the address.
              </p>
              {!dupGuard.local && dupGuard.supa && (
                <p style={{ margin: 0, fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                  This address is saved on your listing form (beds, baths, sqft) — that's the copy that loads.
                </p>
              )}
              {dupGuard.local && !dupGuard.supa && (
                <p style={{ margin: 0, fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                  Loads your saved Property Info copy.
                </p>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="act-btn" onClick={loadSavedFromGuard} style={{ flex: 1, minWidth: 150 }}>
                  Load saved (free)
                </button>
                <button className="act-btn" onClick={() => { setDupGuard(null); runPropertyLookup(); }} style={{ flex: 1, minWidth: 150 }}>
                  Refresh data (uses 1 lookup)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isDemoMode && (
        <div style={{
          position: "sticky",
          top: 0,
          zIndex: 200,
          background: "linear-gradient(90deg, #d4915c 0%, #c97f47 100%)",
          color: "#fff",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
          fontFamily: "'Geist', sans-serif",
          fontSize: 13,
          fontWeight: 500,
          flexWrap: "wrap",
          textAlign: "center",
        }}>
          <span>
            {demoCapReached
              ? `You've used all ${DEMO_GEN_CAP} demo generations. Sign up to continue.`
              : `You're exploring Pitcherly as a guest · ${DEMO_GEN_CAP - demoGenUsed} of ${DEMO_GEN_CAP} demo generations left`}
          </span>
          <button
            onClick={onExitDemo}
            style={{
              background: "#fff",
              color: "#c97f47",
              border: "none",
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'Geist', sans-serif",
            }}
          >
            Start Free Trial &rarr;
          </button>
        </div>
      )}
      <div className="app">

        {/* MOBILE SIDEBAR TOGGLE */}
        <button className="mobile-sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">&#9776;</button>
        <div className={`sidebar-backdrop ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />

        {/* SIDEBAR */}
        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <button
            className="sidebar-mobile-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              width: 44,
              height: 44,
              padding: '8px',
              margin: '12px 0 0 8px',
              fontSize: '20px',
              color: '#e8e4dd',
              fontFamily: "'Geist', sans-serif"
            }}
          >✕</button>
          <div className="sidebar-logo" style={{whiteSpace:'nowrap'}}>Pitcher<em>ly</em></div>
          {SIDEBAR_GROUPS.map(group => (
            <div key={group.label} className="sidebar-group">
              <div className="sidebar-label">{group.label}</div>
              <div className="sidebar-tabs">
                {group.tabs.map(t => (
                  <button key={t.id}
                    className={`sidebar-tab ${tab === t.id ? "active" : ""}`}
                    onClick={() => { setTab(t.id); setError(null); setTriedSubmit(false); setSidebarOpen(false); setEmailTo(""); setSelectedContacts([]); }}>
                    <span className="tab-icon">{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="sidebar-bottom">
            {isDemoMode && (() => {
              const sbUsed = demoGenUsed;
              const sbLimit = DEMO_GEN_CAP;
              const sbPct = (sbUsed / sbLimit) * 100;
              return (
                <div className="usage-card">
                  <div style={{fontSize:11,color:'#888',marginBottom:4,letterSpacing:'0.05em'}}>DEMO MODE</div>
                  <div style={{fontSize:13,color:'#e8e4dd',fontWeight:600,marginBottom:6}}>
                    {sbUsed} / {sbLimit} demo generations
                  </div>
                  <div style={{height:3,background:'#2a2a2a',borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.min(sbPct,100)}%`,background: sbPct >= 100 ? '#ef4444' : '#d4915c',borderRadius:2,transition:'width 0.3s ease'}} />
                  </div>
                  <button className="upgrade-btn" style={{marginTop:10}} onClick={onExitDemo}>Sign Up to Continue</button>
                </div>
              );
            })()}
            {!isDemoMode && (() => {
              const sbLimit = subscription?.stripe_status === "trialing" ? 50
                : subscription?.plan === "pro" ? Infinity
                : subscription?.plan === "lite" ? 50
                : 200;
              const sbUsed = usageCount || 0;
              const sbPct = sbLimit === Infinity ? 0 : (sbUsed / sbLimit) * 100;
              const sbLabel = subscription?.stripe_status === "trialing"
                ? "FREE TRIAL"
                : (subscription?.plan || "starter").toUpperCase();
              return (
                <div className="usage-card">
                  <div style={{fontSize:11,color:'#888',marginBottom:4,letterSpacing:'0.05em'}}>{sbLabel}</div>
                  <div style={{fontSize:13,color:'#e8e4dd',fontWeight:600,marginBottom:6}}>
                    {sbLimit === Infinity ? 'Unlimited ✦' : usageLoading ? `— / ${sbLimit} used` : `${sbUsed} / ${sbLimit} used`}
                  </div>
                  <div style={{height:3,background:'#2a2a2a',borderRadius:2,overflow:'hidden'}}>
                    <div style={{
                      height:'100%',
                      width:`${Math.min(sbPct,100)}%`,
                      background: sbPct > 80 ? '#ef4444' : '#d4915c',
                      borderRadius:2,
                      transition:'width 0.3s ease'
                    }} />
                  </div>
                  {sbLimit !== Infinity && sbUsed >= sbLimit && isTrial && (
                    <>
                      <div className="upgrade-msg" style={{marginTop:10}}>Trial limit reached.</div>
                      <button className="upgrade-btn" onClick={() => setTab("account")}>Start paying today — no trial</button>
                      <button className="upgrade-btn" style={{marginTop:6,background:'transparent',color:'var(--text2)',borderColor:'var(--border)'}} onClick={() => setTab("account")}>Upgrade to Pro — Unlimited</button>
                    </>
                  )}
                  {sbLimit !== Infinity && sbUsed >= sbLimit && !isTrial && (
                    <>
                      <div className="upgrade-msg" style={{marginTop:10}}>Monthly limit reached.</div>
                      <button className="upgrade-btn" onClick={() => setTab("account")}>Upgrade to Pro — Unlimited</button>
                    </>
                  )}
                </div>
              );
            })()}
            <div className="agent-setting-label">Agent Settings</div>
            <div className="agent-name-row">
              <input
                value={savedAgentName}
                onChange={e => setSavedAgentName(e.target.value)}
                placeholder="Your name"
              />
              <span className="pencil">✏️</span>
            </div>
            <div style={{marginTop:8}}>
              <label style={{display:'block',fontSize:11,color:'#888',marginBottom:4,fontWeight:600,letterSpacing:0.3}}>Email sign-off</label>
              <input
                list="signoff-options"
                value={savedSignoff}
                onChange={e => setSavedSignoff(e.target.value)}
                placeholder="Best"
                style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',outline:'none',fontFamily:"'Geist', sans-serif",boxSizing:'border-box'}}
              />
              <datalist id="signoff-options">
                <option value="Best" />
                <option value="Best regards" />
                <option value="Regards" />
                <option value="Kind regards" />
                <option value="Warm regards" />
                <option value="Sincerely" />
                <option value="Warmly" />
                <option value="Cheers" />
                <option value="Thanks" />
                <option value="Thank you" />
                <option value="Talk soon" />
                <option value="All the best" />
              </datalist>
              <p style={{fontSize:10,color:'#666',margin:'4px 0 0 0',lineHeight:1.4}}>Used on all generated emails (follow-ups, open house, expired, FSBO, seller update, just sold).</p>
            </div>
            <div style={{marginTop:10}}>
              <label style={{display:'block',fontSize:11,color:'#888',marginBottom:4,fontWeight:600,letterSpacing:0.3}}>Company name</label>
              <input
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="e.g. Coastal Realty Group"
                style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',outline:'none',fontFamily:"'Geist', sans-serif",boxSizing:'border-box'}}
              />
            </div>
            <div style={{marginTop:10}}>
              <label style={{display:'block',fontSize:11,color:'#888',marginBottom:4,fontWeight:600,letterSpacing:0.3}}>Logo</label>
              {logoData ? (
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <img src={logoData} alt="Your logo" style={{maxHeight:36,maxWidth:120,objectFit:'contain',background:'#fff',borderRadius:4,padding:3,boxSizing:'border-box'}} />
                  <button onClick={() => setLogoData("")} title="Remove logo" aria-label="Remove logo" style={{background:'none',border:'1px solid #2a2a2a',borderRadius:6,color:'#888',cursor:'pointer',fontSize:12,padding:'5px 9px',fontFamily:"'Geist', sans-serif"}}>✕ Remove</button>
                </div>
              ) : (
                <button onClick={() => logoInputRef.current && logoInputRef.current.click()} style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',cursor:'pointer',fontFamily:"'Geist', sans-serif",textAlign:'left'}}>＋ Upload logo</button>
              )}
              <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" style={{display:'none'}} onChange={handleLogoFile} />
              <p style={{fontSize:10,color:'#666',margin:'4px 0 0 0',lineHeight:1.4}}>PNG or JPEG under 1MB. Appears on your Save-PDF exports. Saved on this device only.</p>
            </div>
            <div style={{marginTop:10}}>
              <label style={{display:'block',fontSize:11,color:'#888',marginBottom:4,fontWeight:600,letterSpacing:0.3}}>Headshot</label>
              {headshotData ? (
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <img src={headshotData} alt="Your headshot" style={{width:36,height:36,objectFit:'cover',borderRadius:'50%',border:'1px solid #2a2a2a'}} />
                  <button onClick={() => setHeadshotData("")} title="Remove headshot" aria-label="Remove headshot" style={{background:'none',border:'1px solid #2a2a2a',borderRadius:6,color:'#888',cursor:'pointer',fontSize:12,padding:'5px 9px',fontFamily:"'Geist', sans-serif"}}>✕ Remove</button>
                </div>
              ) : (
                <button onClick={() => headshotInputRef.current && headshotInputRef.current.click()} style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',cursor:'pointer',fontFamily:"'Geist', sans-serif",textAlign:'left'}}>＋ Upload headshot</button>
              )}
              <input ref={headshotInputRef} type="file" accept="image/png,image/jpeg" style={{display:'none'}} onChange={handleHeadshotFile} />
              <p style={{fontSize:10,color:'#666',margin:'4px 0 0 0',lineHeight:1.4}}>Round photo on the CMA report cover. PNG or JPEG under 1MB. Saved on this device only.</p>
            </div>
          </div>
          <hr className="sidebar-account-divider" />
          <button
            className={`sidebar-account-tab ${tab === "account" ? "active" : ""}`}
            onClick={() => { setTab("account"); setError(null); setTriedSubmit(false); setSidebarOpen(false); }}>
            <span className="tab-icon">⚙</span> Account Settings
          </button>
          <hr className="sidebar-account-divider" />
          <button
            className={`sidebar-account-tab ${tab === "coming" ? "active" : ""}`}
            onClick={() => { setTab("coming"); setError(null); setTriedSubmit(false); setSidebarOpen(false); }}>
            <span className="tab-icon">🚀</span> Plans & Features
          </button>
          <div style={{marginTop:"auto",padding:"16px 12px 12px",borderTop:"1px solid var(--border)",textAlign:"center",fontSize:11,color:"#555",lineHeight:1.8}}>
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color:"#555",textDecoration:"none"}}>Terms of Service</a>
            {" · "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#555",textDecoration:"none"}}>Privacy Policy</a>
            <br />
            <a href="mailto:support@pitcherly.com" style={{color:"#555",textDecoration:"none"}}>support@pitcherly.com</a>
            <br />
            &copy; 2026 Pitcherly
          </div>
        </aside>

        {/* MAIN */}
        <div className="main-content" key={tab} role="main">

          {/* ACCOUNT SETTINGS PAGE */}
          {tab === "account" && (isDemoMode
            ? <DemoLockedPanel title="Account Settings" body="Sign up to create an account and manage your subscription." onSignUp={onExitDemo} />
            : <AccountPage subscription={subscription} userId={userId} userEmail={userEmail} onLogout={onLogout} usageCount={usageCount} />
          )}

          {/* PLANS & FEATURES PAGE */}
          {tab === "coming" && <ComingSoonPage userEmail={userEmail} subscription={subscription} userId={userId} />}

          {/* CONTACTS PAGE */}
          {tab === "contacts" && (
            <Suspense fallback={<div className="success-page"><div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} /></div>}>
              <ContactsPage onUseContact={onUseContact} />
            </Suspense>
          )}

          {/* PROPERTY INFO PAGE */}
          {tab === "propinfo" && isDemoMode && (
            <DemoLockedPanel title="Property Lookup" body="Verified property lookups are a paid feature. Sign up for the 7-day free trial to unlock." onSignUp={onExitDemo} />
          )}
          {tab === "propinfo" && !isDemoMode && (() => {
            const piS = {padding:'8px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',fontSize:13,outline:'none',fontFamily:"'Geist', sans-serif",width:'100%',boxSizing:'border-box'};
            const piL = {fontSize:11,color:'#888',fontWeight:500,marginBottom:3};
            const piTag = (c) => ({display:'inline-block',padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:`${c}22`,color:c,marginBottom:6});
            const EMPTY_PROP = { address: "", source: "manual", sqft: "", bedrooms: "", bathrooms: "", halfBaths: "", lotSize: "", yearBuilt: "", propertyType: "", lastSaleDate: "", lastSalePrice: "", assessedValue: "", taxAmount: "", neighborhood: "", notes: "" };
            const piSet = (field, val) => { setPiData(prev => ({ ...(prev || EMPTY_PROP), [field]: val })); setPiSaved(false); };
            const startNewProperty = () => { setPiData({ ...EMPTY_PROP, address: piAddress.trim() || "" }); setPiNotFound(false); setPiSaved(false); setPiValidationFailed(false); };
            const filteredProps = piProperties.filter(p => !piSearch || (p.address || "").toLowerCase().includes(piSearch.toLowerCase()));
            return (
            <div className="layout">
              <div className="input-panel">
                <div>
                  <div className="panel-title">Property Info</div>
                  <div className="panel-sub">Look up any property to view and save its details.</div>
                </div>
                <div className="section">
                  <div className="section-head"><span>🔍 Property Lookup</span></div>
                  <div className="section-body">
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={piAddress} onChange={setPiAddress} placeholder="123 Main St, City, State" />
                    </div>
                    {isTrial && (
                      <div style={{background:'rgba(212,145,92,0.08)',border:'1px solid var(--accent)',borderRadius:10,padding:'14px 16px',textAlign:'center'}}>
                        <p style={{margin:'0 0 4px 0',fontSize:14,fontWeight:600,color:'var(--text)'}}>🔒 Instant property data available on Starter and Pro plans</p>
                        <p style={{margin:'0 0 10px 0',fontSize:12,color:'var(--text2)',lineHeight:1.4}}>Enter your property details manually below, or upgrade for verified RentCast lookups.</p>
                        <button onClick={() => setTab("account")} style={{padding:'8px 20px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:600,fontFamily:"'Geist', sans-serif"}}>Upgrade Plan</button>
                      </div>
                    )}
                    {isTrial && (
                      <p style={{margin:'4px 0 0 0',fontSize:11,color:'var(--text3)',lineHeight:1.5}}>Property lookup uses RentCast to pull verified data including square footage, sale history, valuation, and more. Available on Starter (10 lookups/mo) and Pro (50 lookups/mo) plans.</p>
                    )}
                    {!isTrial && (
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 0'}}>
                        <span style={{fontSize:11,color:'#888'}}>{propLookupsUsed} / {propLookupsLimit} verified lookups this month</span>
                        {propLookupsUsed >= propLookupsLimit && <span style={{fontSize:10,color:'#ef4444',fontWeight:600}}>LIMIT REACHED</span>}
                      </div>
                    )}
                    {!isUnlimited && usesRemaining <= 0 && (
                      <div style={{background:'rgba(212,145,92,0.08)',border:'1px solid var(--accent)',borderRadius:10,padding:'14px 16px',textAlign:'center'}}>
                        <p style={{margin:'0 0 8px 0',fontSize:14,fontWeight:600,color:'var(--text)'}}>
                          {isTrial ? "You've used all 50 trial generations" : "You've used all your generations this month"}
                        </p>
                        <button onClick={() => setTab("account")} style={{padding:'10px 24px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif"}}>Upgrade Plan</button>
                      </div>
                    )}
                    {!isTrial && (
                      <div style={{display:'flex',gap:8}}>
                        <button className={`gen-btn ${!piAddress.trim() || piLoading || (!isUnlimited && usesRemaining <= 0) ? "muted" : ""}`} onClick={requestPropertyLookup} disabled={!piAddress.trim() || piLoading || (!isUnlimited && usesRemaining <= 0)} style={{flex:1}}>
                          {piLoading ? <><span className="spinner" style={{width:16,height:16,borderWidth:2}}/> Looking up...</> : (!isUnlimited && usesRemaining <= 0) ? "Limit Reached — Upgrade" : "🔍 Look Up Property"}
                        </button>
                        <button onClick={startNewProperty} style={{padding:'10px 16px',background:'#1a1a1a',border:'1px solid #2a2a2a',color:'#e8e4dd',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",whiteSpace:'nowrap'}}>
                          + Add New Property
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Saved Properties with search */}
                <div className="section">
                  <div className="section-head"><span>📁 Saved Properties ({piProperties.length})</span></div>
                  <div style={{padding:8,borderBottom:'1px solid #1f1f1f'}}>
                    <input value={piSearch} onChange={e => setPiSearch(e.target.value)} placeholder="Search saved properties..." style={{width:'100%',fontSize:12,padding:'7px 10px',background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:6,color:'#e8e4dd',outline:'none',fontFamily:"'Geist', sans-serif",boxSizing:'border-box'}} />
                  </div>
                  <div style={{maxHeight:220,overflowY:'auto'}}>
                    {filteredProps.length === 0 && <div style={{padding:'14px',fontSize:12,color:'#555',textAlign:'center'}}>{piProperties.length === 0 ? "No saved properties yet" : "No matches"}</div>}
                    {filteredProps.map(p => {
                      const srcColor = p.source === "rentcast" ? '#22c55e' : '#666';
                      const srcLabel = p.source === "rentcast" ? 'RentCast' : 'Manual';
                      return (
                      <div key={p.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',borderBottom:'1px solid #1f1f1f',cursor:'pointer'}} onClick={() => loadPiProperty(p)} onMouseEnter={e => e.currentTarget.style.background='#1a1a1a'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <div>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:13,color:'#e8e4dd',fontWeight:500}}>{p.address}</span>
                            <span style={{fontSize:9,padding:'1px 5px',borderRadius:3,background:`${srcColor}22`,color:srcColor,fontWeight:600,letterSpacing:'0.03em'}}>{srcLabel}</span>
                          </div>
                          <div style={{fontSize:11,color:'#666',marginTop:2}}>{[p.propertyType, p.bedrooms && `${p.bedrooms} bd`, p.bathrooms && `${p.bathrooms} ba`].filter(Boolean).join(" · ") || "No details"}</div>
                          {(p.lastLookedUpAt || p.createdAt) && (
                            <div style={{fontSize:10,color:'#555',marginTop:2}}>
                              {p.lastLookedUpAt ? `Looked up ${fmtStamp(p.lastLookedUpAt)}` : `Saved ${fmtStamp(p.createdAt)}`}
                            </div>
                          )}
                        </div>
                        <button aria-label="Delete property" onClick={e => { e.stopPropagation(); if (window.confirm("Delete this property?")) deletePiProperty(p.id); }} style={{background:'none',border:'none',color:'#555',cursor:'pointer',fontSize:14,padding:4}} title="Delete">🗑</button>
                      </div>);
                    })}
                  </div>
                </div>
                {isTrial && (
                  <button onClick={startNewProperty} style={{width:'100%',padding:'12px 16px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",marginTop:8}}>
                    + Add New Property
                  </button>
                )}
              </div>

              {/* RIGHT PANEL — always visible */}
              <div className={`output-panel ${piLoading ? "pulsing" : ""}`}>
                {piLoading && (
                  <div className="shimmer-box">
                    <div className="shimmer" style={{width:"55%",height:20,marginBottom:8}} />
                    <div className="shimmer" style={{width:"100%"}} />
                    <div className="shimmer" style={{width:"90%"}} />
                    <div className="shimmer" style={{width:"70%"}} />
                    <div className="shimmer-label">Looking up property...</div>
                  </div>
                )}

                {!piLoading && !piData && (
                  <div style={{padding:'60px 20px',textAlign:'center',display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
                    <div style={{fontSize:40,opacity:0.4}}>🏠</div>
                    <div style={{fontSize:16,fontWeight:600,color:'var(--text)'}}>No property selected</div>
                    <div style={{fontSize:13,color:'var(--text3)',maxWidth:340,lineHeight:1.5}}>
                      {isTrial
                        ? "Add a property manually to save its details and use them across your listings."
                        : "Look up an address for instant details, or add a new property manually."}
                    </div>
                    <button onClick={startNewProperty} style={{marginTop:8,padding:'10px 24px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif"}}>
                      + Add New Property
                    </button>
                  </div>
                )}

                {!piLoading && piData && (() => {
                  const d = piData;
                  const fieldChange = (field, val) => { piSet(field, val); };
                  return (
                  <div style={{padding:20,display:'flex',flexDirection:'column',gap:16}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                      <div>
                        <div style={{fontSize:16,fontWeight:600,color:'var(--text)'}}>{d.address || piAddress || "New Property"}</div>
                        {d.source === "rentcast" && <div style={{fontSize:11,color:'#22c55e',marginTop:2}}>✓ Verified — Powered by RentCast</div>}
                        {(!piData || d.source === "manual") && !piNotFound && <div style={{fontSize:11,color:'#666',marginTop:2}}>Enter details manually or look up an address</div>}
                        {piData?.id && <div style={{fontSize:11,color:'var(--accent)',marginTop:2}}>Editing saved property</div>}
                      </div>
                      <button onClick={() => { const saveData = piData || { ...d, address: piAddress.trim() || "" }; if (!saveData.address) saveData.address = piAddress.trim(); savePiProperty(saveData); }} className={`update-btn ${piSaved ? "saved" : ""}`}>{piSaved ? "✓ Saved" : piData?.id ? "Update Property" : "Save New Property"}</button>
                    </div>

                    {d.market_value && d.market_value.estimate != null && (
                      <div style={{background:'linear-gradient(135deg, rgba(212,145,92,0.14), rgba(212,145,92,0.05))',border:'1px solid var(--accent)',borderRadius:12,padding:'16px 18px'}}>
                        <div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4}}>Estimated Market Value</div>
                        <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
                          <span style={{fontSize:28,fontWeight:700,color:'var(--accent)',lineHeight:1.1}}>{fmtMoney(d.market_value.estimate)}</span>
                          {d.market_value.rangeLow != null && d.market_value.rangeHigh != null && (
                            <span style={{fontSize:13,color:'var(--text2)'}}>range {fmtK(d.market_value.rangeLow)}–{fmtK(d.market_value.rangeHigh)}</span>
                          )}
                        </div>
                        <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Powered by RentCast AVM — today's likely market value (not the tax-assessed value below).</div>
                      </div>
                    )}

                    {piValidationFailed && (
                      <div style={{background:'rgba(239,68,68,0.08)',border:'1px solid #ef4444',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#ef4444'}}>
                        Please fill in all required fields before saving.
                      </div>
                    )}

                    {piNotFound && (
                      <div style={{background:'#1a1700',border:'1px solid #f59e0b',borderRadius:8,padding:'10px 14px',display:'flex',alignItems:'flex-start',gap:8}}>
                        <span>ℹ️</span>
                        <p style={{fontSize:12,color:'#f59e0b',margin:0,lineHeight:1.5}}>We couldn't find details for this property. Enter the details manually below.</p>
                      </div>
                    )}

                    {/* Overview */}
                    <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14}}>
                      <div style={piTag('#3b82f6')}>Overview</div>
                      <div className="pi-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <div><div style={piL}>Property Type *</div><input style={{...piS, ...(piValidationFailed && !d.propertyType?.trim() ? {borderColor:'var(--red)'} : {})}} value={d.propertyType || ""} onChange={e => fieldChange("propertyType", e.target.value)} placeholder="e.g. Single Family" /></div>
                        <div><div style={piL}>Year Built</div><input style={piS} inputMode="numeric" value={d.yearBuilt || ""} onChange={e => fieldChange("yearBuilt", e.target.value)} placeholder="e.g. 1998" /></div>
                      </div>
                    </div>

                    {/* Size */}
                    <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14}}>
                      <div style={piTag('#22c55e')}>Size</div>
                      <div className="pi-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <div><div style={piL}>Sq Ft *</div><input style={{...piS, ...(piValidationFailed && !d.sqft?.trim() ? {borderColor:'var(--red)'} : {})}} inputMode="numeric" value={d.sqft || ""} onChange={e => fieldChange("sqft", e.target.value)} placeholder="e.g. 2400" /></div>
                        <div><div style={piL}>Bedrooms *</div><input style={{...piS, ...(piValidationFailed && !d.bedrooms?.trim() ? {borderColor:'var(--red)'} : {})}} inputMode="numeric" value={d.bedrooms || ""} onChange={e => fieldChange("bedrooms", e.target.value)} placeholder="e.g. 4" /></div>
                        <div><div style={piL}>Bathrooms *</div><input style={{...piS, ...(piValidationFailed && !d.bathrooms?.trim() ? {borderColor:'var(--red)'} : {})}} inputMode="numeric" value={d.bathrooms || ""} onChange={e => fieldChange("bathrooms", e.target.value)} placeholder="e.g. 3" /></div>
                        <div><div style={piL}>Half Baths</div><input style={piS} inputMode="numeric" value={d.halfBaths || ""} onChange={e => fieldChange("halfBaths", e.target.value)} placeholder="e.g. 1" /></div>
                        <div style={{gridColumn:'1 / -1'}}><div style={piL}>Lot Size</div><input style={piS} value={d.lotSize || ""} onChange={e => fieldChange("lotSize", e.target.value)} placeholder="e.g. 8500 sqft" /></div>
                      </div>
                    </div>

                    {/* Sale History */}
                    <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14}}>
                      <div style={piTag('#f59e0b')}>Sale History</div>
                      <div className="pi-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <div><div style={piL}>Last Sale Date</div><input style={piS} value={d.lastSaleDate || ""} onChange={e => fieldChange("lastSaleDate", e.target.value)} placeholder="e.g. 2021-06-15" /></div>
                        <div><div style={piL}>Last Sale Price</div><input style={piS} inputMode="decimal" value={d.lastSalePrice || ""} onChange={e => fieldChange("lastSalePrice", e.target.value)} placeholder="e.g. $450,000" /></div>
                      </div>
                    </div>

                    {/* Valuation */}
                    <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14}}>
                      <div style={piTag('#d4915c')}>Valuation</div>
                      <div className="pi-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <div><div style={piL}>Tax Assessed Value</div><input style={piS} inputMode="decimal" value={d.assessedValue || ""} onChange={e => fieldChange("assessedValue", e.target.value)} placeholder="e.g. $420,000" /></div>
                        <div><div style={piL}>Tax Amount</div><input style={piS} inputMode="decimal" value={d.taxAmount || ""} onChange={e => fieldChange("taxAmount", e.target.value)} placeholder="e.g. $5,200/yr" /></div>
                      </div>
                      <div style={{fontSize:11,color:'var(--text3)',marginTop:8,lineHeight:1.5}}>County tax valuation — often far below market value (especially CA Prop 13). See Estimated Market Value above for what it's worth today.</div>
                    </div>

                    {/* Neighborhood */}
                    <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14}}>
                      <div style={piTag('#888')}>Neighborhood</div>
                      <div><div style={piL}>Area Description</div><input style={piS} value={d.neighborhood || ""} onChange={e => fieldChange("neighborhood", e.target.value)} placeholder="Surrounding area, nearby amenities..." /></div>
                      <div style={{marginTop:8}}><div style={piL}>Notes</div><input style={piS} value={d.notes || ""} onChange={e => fieldChange("notes", e.target.value)} placeholder="Personal notes about this property..." /></div>
                    </div>

                    {piData?.id && (
                      <button onClick={() => { if (window.confirm("Are you sure? This cannot be undone.")) deletePiProperty(piData.id); }} style={{padding:'10px 16px',background:'transparent',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:600,fontFamily:"'Geist', sans-serif",opacity:0.7,transition:'opacity 0.15s'}} onMouseEnter={e => e.currentTarget.style.opacity='1'} onMouseLeave={e => e.currentTarget.style.opacity='0.7'}>
                        Delete Property
                      </button>
                    )}
                  </div>
                  );
                })()}
              </div>
            </div>
            );
          })()}

          {/* NORMAL TOOL TABS */}
          {tab !== "account" && tab !== "coming" && tab !== "contacts" && tab !== "propinfo" && <div className="layout">
            <div className="input-panel">
              <div>
                <div className="panel-title">
                  {tab === "listing" ? "Property Details" : tab === "followup" ? "Lead Details" : tab === "contract" ? "Contract" : tab === "inspection" ? "Inspection Report" : tab === "openhouse" ? "Listing Tools" : tab === "sellerupdate" ? "Seller Update" : tab === "bio" ? "Agent Bio" : tab === "expired" ? "Expired Outreach" : tab === "fsbo" ? "FSBO Outreach" : tab === "offer" ? "Offer Summary" : tab === "cma" ? "CMA Report" : ""}
                </div>
                <div className="panel-sub">
                  {tab === "listing" && "Fill in the details and get professional copy in seconds."}
                  {tab === "followup" && "Enter the lead's info and get 3 tailored follow-up emails."}
                  {tab === "contract" && "Paste any contract and get a clear, friendly summary in seconds."}
                  {tab === "inspection" && "Paste an inspection report and get a severity-triaged summary + a repair ask."}
                  {tab === "openhouse" && "Open house follow-ups and just sold announcements."}
                  {tab === "sellerupdate" && "Create market update reports for your sellers."}
                  {tab === "bio" && "Generate a professional agent bio."}
                  {tab === "expired" && "Reach out to homeowners with expired listings."}
                  {tab === "fsbo" && "Connect with For Sale By Owner homeowners."}
                  {tab === "offer" && "Draft a fact-only, Fair-Housing-safe offer cover for the listing agent."}
                  {tab === "cma" && "Generate a comparative market analysis narrative."}
                </div>
              </div>

              {/* LISTING */}
              {tab === "listing" && (<>
                <div className="section">
                  <div className="section-head">
                    <span>🏠 Property Info</span>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      {renderPropertyPicker("listing")}
                      <button className={`update-btn ${propertySaved ? "saved" : ""}`} onClick={updateProperty}>
                        {propertySaved ? "✓ Synced" : "Update Property"}
                      </button>
                    </div>
                  </div>
                  <div className="section-body">
                    <div className={`field ${triedSubmit && !address.trim() ? "required-miss" : ""}`}>
                      <label>Address *</label>
                      <AddressInput placeholder="124 Maple Drive, Riverside CA" value={address} onChange={setAddress} />
                    </div>
                    <div className="row-3">
                      <div className="field"><label>Bedrooms</label><input type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>Bathrooms</label><input type="number" value={bathrooms} onChange={e => setBathrooms(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>Half Baths</label><input type="number" min="0" value={halfBaths} onChange={e => setHalfBaths(e.target.value)} disabled={loading} /></div>
                    </div>
                    <div className="row">
                      <div className="field"><label>Sq Footage</label><input inputMode="numeric" value={sqft} onChange={e => setSqft(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>List Price</label><input inputMode="decimal" placeholder="e.g. $450,000" value={price} onChange={e => setPrice(e.target.value)} disabled={loading} /></div>
                    </div>
                    <div className="field">
                      <label>Key Features</label>
                      <textarea value={features} onChange={e => setFeatures(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>

                {/* TONE SECTION */}
                <div className="section">
                  <div className="section-head">◈ Tone</div>
                  <div className="section-body">
                    <div className="pills">
                      {TONES.map(t => (
                        <button key={t}
                          className={`pill ${tone === t ? "on" : ""}`}
                          onClick={() => { setTone(t); setActiveToneLabel(t); setCustomToneOpen(false); }}>{t}</button>
                      ))}
                      <button
                        className={`pill ${tone === "custom" ? "on" : ""}`}
                        onClick={() => { setCustomToneOpen(!customToneOpen); if (tone !== "custom") setTone(""); }}>
                        {tone === "custom" ? `✦ ${activeToneLabel}` : "+ Custom"}
                      </button>
                    </div>
                    <div className={`custom-tone-wrap ${customToneOpen ? "open" : ""}`}>
                      <div className="custom-tone-input">
                        <input
                          placeholder="e.g. Warm and conversational, like a trusted neighbor..."
                          value={customToneInput}
                          onChange={e => setCustomToneInput(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && applyCustomTone()}
                          disabled={loading}
                        />
                        <button className="apply-btn" onClick={applyCustomTone}>Apply</button>
                      </div>
                      <div className="tone-hint">Describe your tone in plain English. The AI will write to match it exactly.</div>
                    </div>
                    {tone === "custom" && !customToneOpen && (
                      <div className="custom-badge">
                        ✦ {activeToneLabel}
                        <button aria-label="Clear custom tone" onClick={clearCustomTone}>×</button>
                      </div>
                    )}
                  </div>
                </div>
              </>)}

              {/* FOLLOWUP */}
              {tab === "followup" && (
                <div className="section">
                  <div className="section-head"><span>✉️ Lead Info</span><div style={{display:'flex',gap:6}}>{renderPropertyPicker("followup")}{renderContactPicker("followup")}</div></div>
                  <div className="section-body">
                    <div className="row">
                      <div className={`field ${triedSubmit && !leadName.trim() ? "required-miss" : ""}`}>
                        <label>Lead's Name *</label>
                        <input value={leadName} onChange={e => setLeadName(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Your Name</label>
                        <input value={agentName} onChange={e => setAgentName(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Property They Inquired About</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="field">
                      <label>Buyer Stage</label>
                      <div className="pills">
                        {BUYER_STAGES.map(s => (
                          <button key={s} className={`pill ${stage === s ? "on" : ""}`} onClick={() => setStage(s)}>{s}</button>
                        ))}
                      </div>
                    </div>
                    <div className="field">
                      <label>Additional Context (optional)</label>
                      <textarea placeholder="Any notes about this lead..." value={leadContext} onChange={e => setLeadContext(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>
              )}

              {/* CONTRACT */}
              {tab === "contract" && (
                <div className="section">
                  <div className="section-head">📋 Contract Text</div>
                  <div className="section-body">
                    <div className="synced-hint" style={{marginBottom:8}}>Property: <span>{sharedProperty.address}</span> · {sharedProperty.bedrooms}bd / {sharedProperty.bathrooms}ba · {sharedProperty.price}</div>
                    <div className={`field ${triedSubmit && !contractText.trim() ? "required-miss" : ""}`}>
                      <label>Paste Contract Below *</label>
                      <textarea style={{minHeight: 180}} value={contractText} onChange={e => setContractText(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>
              )}

              {/* INSPECTION */}
              {tab === "inspection" && (
                <div className="section">
                  <div className="section-head">🔍 Inspection Report Text</div>
                  <div className="section-body">
                    <div className={`field ${triedSubmit && !reportText.trim() ? "required-miss" : ""}`}>
                      <label>Paste Inspection Report Below *</label>
                      <textarea style={{minHeight: 220}} value={reportText} onChange={e => setReportText(e.target.value)} disabled={loading} />
                    </div>
                    <div className="synced-hint" style={{marginTop:8}}>Paste the report text (open the PDF → Select All → Copy). A summary aid for agents — not a home inspection.</div>
                  </div>
                </div>
              )}

              {/* OFFER SUMMARY */}
              {tab === "offer" && (
                <div className="section">
                  <div className="section-head">📨 Offer Details</div>
                  <div className="section-body">
                    <div className="row">
                      <div className={`field ${triedSubmit && !buyerName.trim() ? "required-miss" : ""}`}>
                        <label>Buyer Name(s) *</label>
                        <input placeholder="e.g. Alex & Jordan Carter" value={buyerName} onChange={e => setBuyerName(e.target.value)} disabled={loading} />
                      </div>
                      <div className={`field ${triedSubmit && !offerPrice.trim() ? "required-miss" : ""}`}>
                        <label>Offer Price *</label>
                        <input placeholder="e.g. $485,000" value={offerPrice} onChange={e => setOfferPrice(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className={`field ${triedSubmit && !sharedProperty.address.trim() ? "required-miss" : ""}`}>
                      <label>Property Address *</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="row">
                      <div className="field">
                        <label>Earnest Money</label>
                        <input placeholder="e.g. $15,000" value={earnestMoney} onChange={e => setEarnestMoney(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Financing</label>
                        <input placeholder="e.g. Conventional, 20% down, pre-approved" value={financing} onChange={e => setFinancing(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="field">
                        <label>Contingencies / Waivers</label>
                        <input placeholder="e.g. 10-day inspection, appraisal waived" value={contingencies} onChange={e => setContingencies(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Close Date / Timeline</label>
                        <input placeholder="e.g. 30 days, flexible rent-back" value={closeDate} onChange={e => setCloseDate(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Additional Factual Notes (optional)</label>
                      <textarea placeholder="Strengths of the offer — terms only, no personal or family details." value={offerNotes} onChange={e => setOfferNotes(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>State (optional)</label>
                      <input placeholder="e.g. OR, CA, TX" value={offerState} onChange={e => setOfferState(e.target.value)} disabled={loading} style={{maxWidth:160}} />
                    </div>
                    <div style={{fontSize:12,lineHeight:1.5,color:'var(--text2)',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:8,padding:'10px 12px',marginTop:4}}>
                      ⚠ <strong>Fact-only by design.</strong> Offer letters that mention a buyer's family, religion, age, or personal traits carry Fair Housing risk — NAR advises agents against emotional "love letters." This drafts terms only, and the result is scanned for protected-class language.
                      {(offerState.trim().toLowerCase() === "or" || /oregon/i.test(offerState)) && (
                        <div style={{marginTop:6,paddingTop:6,borderTop:'1px solid rgba(245,158,11,0.2)'}}>Oregon note: HB 2550 tried to ban buyer letters (later struck down on 1st-Amendment grounds) — a fact-only offer summary like this is the safe form.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* LISTING TOOLS (Open House + Just Sold) */}
              {tab === "openhouse" && (<>
                <div className="section">
                  <div className="section-head"><span>🚪 Open House Follow-Up</span><div style={{display:'flex',gap:6}}>{renderPropertyPicker("openhouse")}{renderContactPicker("openhouse")}</div></div>
                  <div className="section-body">
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="field">
                      <label>Open House Date</label>
                      <input type="date" value={ohDate} onChange={e => setOhDate(e.target.value)} disabled={loading} />
                    </div>
                    <div className={`field ${triedSubmit && !ohVisitorName.trim() ? "required-miss" : ""}`}>
                      <label>Visitor Name *</label>
                      <input placeholder="e.g. Sarah Johnson" value={ohVisitorName} onChange={e => setOhVisitorName(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Visitor Interest Notes (optional)</label>
                      <textarea placeholder="e.g. loved the backyard, has a house to sell first" value={ohNotes} onChange={e => setOhNotes(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>

                <div style={{borderTop:"1px solid var(--border)",margin:"8px 0"}} />

                <div className="section">
                  <div className="section-head"><span>🎉 Just Sold Announcement</span>{renderPropertyPicker("justsold")}</div>
                  <div className="section-body">
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="row">
                      <div className="field">
                        <label>Sale Price *</label>
                        <input placeholder="e.g. $625,000" value={jsSalePrice} onChange={e => setJsSalePrice(e.target.value)} disabled={jsLoading} />
                      </div>
                      <div className="field">
                        <label>Days on Market</label>
                        <input type="number" placeholder="e.g. 12" value={jsDaysOnMarket} onChange={e => setJsDaysOnMarket(e.target.value)} disabled={jsLoading} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Fun Fact (optional)</label>
                      <input placeholder="e.g. 15 offers in 3 days, sold $20K over asking" value={jsFunFact} onChange={e => setJsFunFact(e.target.value)} disabled={jsLoading} />                    </div>
                    <div className="field">
                      <label>Agent Name</label>
                      <input value={savedAgentName} readOnly style={{opacity:0.7}} />
                      <div className="synced-hint" style={{marginTop:4}}>From sidebar settings</div>
                    </div>
                    <button className={`gen-btn ${!jsSalePrice.trim() || jsLoading ? "muted" : ""}`} onClick={runJustSold} disabled={!jsSalePrice.trim() || jsLoading} style={{marginTop:8}}>
                      {jsLoading ? <><span className="spinner" style={{width:16,height:16,borderWidth:2}}/> Generating...</> : "✦ Generate Announcement"}
                    </button>
                    {justSoldResult && (
                      <div className="outbox" style={{marginTop:16}}>
                        <div className="outbox-header">
                          <div className="outbox-label"><div className="status-dot" />Just Sold</div>
                          <div style={{display:'flex',gap:6}}>
                            {["social","text","email"].map(t => (
                              <button key={t} className={`pill ${jsOutTab === t ? "on" : ""}`} onClick={() => setJsOutTab(t)} style={{fontSize:11,padding:'3px 10px'}}>{t === "social" ? "Social" : t === "text" ? "Text" : "Email"}</button>
                            ))}
                          </div>
                        </div>
                        <div className="outbox-body">
                          {jsOutTab === "social" && <OutText>{justSoldResult.social}</OutText>}
                          {jsOutTab === "text" && <OutText>{justSoldResult.text}</OutText>}
                          {jsOutTab === "email" && justSoldResult.email && (
                            <>
                              <div className="out-subject"><strong>Subject:</strong> {justSoldResult.email.subject}</div>
                              <OutText style={{marginTop:8}}>{justSoldResult.email.body}</OutText>
                              {renderSendEmail(justSoldResult.email.subject, justSoldResult.email.body)}
                            </>
                          )}
                          <div style={{display:'flex',gap:8,marginTop:12}}>
                            <button className="act-btn" onClick={() => copy(jsOutTab === "email" && justSoldResult.email ? `Subject: ${justSoldResult.email.subject}\n\n${justSoldResult.email.body}` : justSoldResult[jsOutTab])}>
                              {copied ? "✓ Copied" : "Copy"}
                            </button>
                            <ShareBtn title="Just Sold" text={jsOutTab === "email" && justSoldResult.email ? `Subject: ${justSoldResult.email.subject}\n\n${justSoldResult.email.body}` : justSoldResult[jsOutTab]} />
                            <button className="act-btn" onClick={() => printableExport("Just Sold Announcement", jsOutTab === "email" && justSoldResult.email ? `Subject: ${justSoldResult.email.subject}\n\n${justSoldResult.email.body}` : justSoldResult[jsOutTab])}>Save PDF</button>
                          </div>
                        </div>
                      </div>
                    )}
                    <button onClick={nextVisitor} style={{marginTop:8,padding:'8px 14px',background:'transparent',border:'1px solid var(--border)',color:'var(--text2)',borderRadius:8,cursor:'pointer',fontSize:12,fontFamily:"'Geist', sans-serif"}}>↩ New Announcement</button>
                  </div>
                </div>
              </>)}

              {/* SELLER UPDATE */}
              {tab === "sellerupdate" && (
                <div className="section">
                  <div className="section-head"><span>📊 Seller Update</span><div style={{display:'flex',gap:6}}>{renderPropertyPicker("sellerupdate")}{renderContactPicker("sellerupdate")}</div></div>
                  <div className="section-body">
                    <div className="row">
                      <div className={`field ${triedSubmit && !sellerName.trim() ? "required-miss" : ""}`}>
                        <label>Seller Name *</label>
                        <input placeholder="e.g. Mike & Lisa Chen" value={sellerName} onChange={e => setSellerName(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Agent Name</label>
                        <input value={savedAgentName} readOnly style={{opacity:0.7}} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="row">
                      <div className="field">
                        <label>Days on Market</label>
                        <input type="number" placeholder="e.g. 14" value={daysOnMarket} onChange={e => setDaysOnMarket(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Showings This Week</label>
                        <input type="number" placeholder="e.g. 6" value={showingsThisWeek} onChange={e => setShowingsThisWeek(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Showing Feedback</label>
                      <textarea placeholder="e.g. buyers love the kitchen, some noted the carpet in the bedrooms" value={showingFeedback} onChange={e => setShowingFeedback(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Include Price Reduction Email?</label>
                      <div className="pills">
                        <button className={`pill ${!priceReduction ? "on" : ""}`} onClick={() => setPriceReduction(false)}>No</button>
                        <button className={`pill ${priceReduction ? "on" : ""}`} onClick={() => setPriceReduction(true)}>Yes</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* BIO */}
              {tab === "bio" && (
                <div className="section">
                  <div className="section-head">👤 Agent Bio</div>
                  <div className="section-body">
                    <div className="row">
                      <div className={`field ${triedSubmit && !savedAgentName.trim() ? "required-miss" : ""}`}>
                        <label>Agent Name *</label>
                        <input value={savedAgentName} readOnly style={{opacity:0.7}} />
                        <div className="synced-hint" style={{marginTop:4}}>From sidebar settings</div>
                      </div>
                      <div className="field">
                        <label>Years of Experience</label>
                        <input placeholder="e.g. 8" inputMode="numeric" value={bioYears} onChange={e => setBioYears(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className={`field ${triedSubmit && !bioMarket.trim() ? "required-miss" : ""}`}>
                      <label>City / Market *</label>
                      <input placeholder="e.g. Riverside, CA" value={bioMarket} onChange={e => setBioMarket(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Specialties</label>
                      <div className="pills">
                        {BIO_SPECIALTIES.map(s => (
                          <button key={s} className={`pill ${bioSpecialties.includes(s) ? "on" : ""}`} onClick={() => toggleBioSpecialty(s)}>{s}</button>
                        ))}
                      </div>
                    </div>
                    <div className="field">
                      <label>Personal Fact (what makes you unique)</label>
                      <input placeholder="e.g. former marine, mom of three, lifelong Riverside local" value={bioFact} onChange={e => setBioFact(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Tone</label>
                      <div className="pills">
                        {BIO_TONES.map(t => (
                          <button key={t} className={`pill ${bioTone === t ? "on" : ""}`} onClick={() => setBioTone(t)}>{t}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* EXPIRED */}
              {tab === "expired" && (
                <div className="section">
                  <div className="section-head"><span>📋 Expired Listing Outreach</span>{renderContactPicker("expired")}{renderPropertyPicker("expired")}</div>
                  <div className="section-body">
                    <div className={`field ${triedSubmit && !expiredName.trim() ? "required-miss" : ""}`}>
                      <label>Homeowner Name *</label>
                      <input placeholder="e.g. John Smith" value={expiredName} onChange={e => setExpiredName(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="row">
                      <div className="field">
                        <label>Original List Price</label>
                        <input placeholder="e.g. $599,000" value={expiredPrice} onChange={e => setExpiredPrice(e.target.value)} disabled={loading} />
                      </div>
                      <div className="field">
                        <label>Days on Market</label>
                        <input type="number" placeholder="e.g. 120" value={expiredDays} onChange={e => setExpiredDays(e.target.value)} disabled={loading} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Agent Name</label>
                      <input value={savedAgentName} readOnly style={{opacity:0.7}} />
                      <div className="synced-hint" style={{marginTop:4}}>From sidebar settings</div>
                    </div>
                  </div>
                </div>
              )}

              {/* FSBO */}
              {tab === "fsbo" && (
                <div className="section">
                  <div className="section-head"><span>📤 FSBO Outreach</span>{renderContactPicker("fsbo")}{renderPropertyPicker("fsbo")}</div>
                  <div className="section-body">
                    <div className={`field ${triedSubmit && !fsboName.trim() ? "required-miss" : ""}`}>
                      <label>Homeowner Name *</label>
                      <input placeholder="e.g. Jane Doe" value={fsboName} onChange={e => setFsboName(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Property Address</label>
                      <AddressInput value={sharedProperty.address} onChange={(v) => setSharedProperty(p => ({...p, address: v}))} />
                    </div>
                    <div className="field">
                      <label>Asking Price</label>
                      <input placeholder="e.g. $450,000" value={fsboPrice} onChange={e => setFsboPrice(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Agent Name</label>
                      <input value={savedAgentName} readOnly style={{opacity:0.7}} />
                      <div className="synced-hint" style={{marginTop:4}}>From sidebar settings</div>
                    </div>
                    <div className="field">
                      <label>Context (optional)</label>
                      <textarea placeholder="e.g. noticed their Zillow listing, price seems low for the area..." value={fsboContext} onChange={e => setFsboContext(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>
              )}

              {/* CMA */}
              {tab === "cma" && (
                <div className="section">
                  <div className="section-head"><span>📊 Comparative Market Analysis</span>{renderPropertyPicker("cma")}</div>
                  <div className="section-body">
                    <div className="field">
                      <label>Report for</label>
                      <div style={{display:'flex',gap:6,background:'#0d0d0d',border:'1px solid #2a2a2a',borderRadius:8,padding:4}}>
                        {[["seller","Seller"],["buyer","Buyer"]].map(([m,lbl]) => (
                          <button key={m} onClick={() => setCmaMode(m)} disabled={loading}
                            style={{flex:1,padding:'8px 10px',borderRadius:6,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",background:cmaMode===m?'var(--accent)':'transparent',color:cmaMode===m?'#fff':'var(--text2)'}}>{lbl}</button>
                        ))}
                      </div>
                      <p style={{fontSize:11,color:'var(--text3)',margin:'6px 0 0 0',lineHeight:1.5}}>{cmaMode==="buyer" ? "Offer guidance for a home your client wants to buy." : "Pricing strategy to list this home."}</p>
                    </div>
                    <div className={`field ${triedSubmit && !address.trim() ? "required-miss" : ""}`}>
                      <label>Property Address *</label>
                      <AddressInput value={address} onChange={setAddress} />
                    </div>
                    <div className="row-3">
                      <div className="field"><label>Bedrooms</label><input type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>Bathrooms</label><input type="number" value={bathrooms} onChange={e => setBathrooms(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>Sq Footage</label><input inputMode="numeric" value={sqft} onChange={e => setSqft(e.target.value)} disabled={loading} /></div>
                    </div>
                    <div className="row">
                      <div className="field"><label>{cmaMode === "buyer" ? "Current List Price" : "Current / Asking Price"}</label><input inputMode="decimal" placeholder="e.g. $450,000" value={price} onChange={e => setPrice(e.target.value)} disabled={loading} /></div>
                      <div className="field"><label>Market Area</label><input placeholder="e.g. Riverside, CA 92501" value={cmaMarket} onChange={e => setCmaMarket(e.target.value)} disabled={loading} /></div>
                    </div>
                    <div className="field">
                      <label>Key Features</label>
                      <textarea value={features} onChange={e => setFeatures(e.target.value)} disabled={loading} />
                    </div>
                    <div className="field">
                      <label>Cover Photo <span style={{color:'var(--accent)',fontWeight:600}}>— highly recommended</span></label>
                      {cmaPhoto ? (
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <img src={cmaPhoto} alt="Cover" style={{height:48,maxWidth:90,objectFit:'cover',borderRadius:6,border:'1px solid #2a2a2a'}} />
                          <button onClick={() => setCmaPhoto("")} style={{background:'none',border:'1px solid #2a2a2a',borderRadius:6,color:'#888',cursor:'pointer',fontSize:12,padding:'6px 10px',fontFamily:"'Geist', sans-serif"}}>✕ Remove</button>
                        </div>
                      ) : (
                        <button onClick={() => cmaPhotoInputRef.current && cmaPhotoInputRef.current.click()} style={{width:'100%',fontSize:13,padding:'11px 13px',background:'rgba(212,145,92,0.08)',border:'1px dashed var(--accent)',borderRadius:8,color:'var(--accent)',cursor:'pointer',fontFamily:"'Geist', sans-serif",textAlign:'center',fontWeight:600}}>📷 Upload a front photo of the property</button>
                      )}
                      <input ref={cmaPhotoInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{display:'none'}} onChange={handleCmaPhotoFile} />
                      <p style={{fontSize:11,color:'var(--text3)',margin:'6px 0 0 0',lineHeight:1.5}}>A real front photo makes a polished cover. If none is provided, we'll use a street-level image — or a clean placeholder if no imagery exists.</p>
                    </div>
                    {cmaHasLookups ? (
                      <div style={{marginTop:4,padding:'12px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10}}>
                        <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                          <input type="checkbox" checked={useMarketData} onChange={e => setUseMarketData(e.target.checked)} disabled={loading} style={{accentColor:'var(--accent)',width:16,height:16}} />
                          <span style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Use market data</span>
                          <span style={{marginLeft:'auto',fontSize:11,color:'var(--text3)'}}>{propLookupsUsed} / {propLookupsLimit} lookups used</span>
                        </label>
                        <p style={{margin:'8px 0 0 26px',fontSize:11,color:'var(--text3)',lineHeight:1.5}}>
                          Pulls a live value estimate + comparable sales for this address. Uses 1 property lookup.
                          {useMarketData && propLookupsUsed >= propLookupsLimit && <span style={{color:'var(--accent)'}}> You're out of lookups this month — this CMA will use your entered details only.</span>}
                        </p>
                      </div>
                    ) : (
                      <div style={{marginTop:4,padding:'12px 14px',background:'rgba(212,145,92,0.08)',border:'1px solid var(--accent)',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
                        <span style={{fontSize:12,color:'var(--text2)',lineHeight:1.4}}>Starter and Pro CMAs include live market data and comps.</span>
                        <button onClick={() => setTab("account")} style={{padding:'6px 14px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:600,fontFamily:"'Geist', sans-serif",whiteSpace:'nowrap'}}>Upgrade</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* GENERATE BUTTON (non-openhouse/justsold tabs) */}
              {tab !== "openhouse" && (
                <button
                  className={`gen-btn sticky-cta ${!isReady || loading ? "muted" : ""}`}
                  disabled={!isReady || loading}
                  onClick={run}
                >
                  {loading ? (
                    <><span className="spinner" style={{width:16,height:16,borderWidth:2}}/> Generating...</>
                  ) : (
                    tab === "listing" ? "✦ Generate Listing"
                    : tab === "followup" ? "✦ Generate Follow-Ups"
                    : tab === "contract" ? "✦ Summarize Contract"
                    : tab === "inspection" ? "✦ Summarize Inspection"
                    : tab === "sellerupdate" ? "✦ Generate Update"
                    : tab === "bio" ? "✦ Generate Bio"
                    : tab === "expired" ? "✦ Generate Outreach"
                    : tab === "fsbo" ? "✦ Generate Outreach"
                    : tab === "offer" ? "✦ Draft Offer Summary"
                    : tab === "cma" ? "✦ Generate CMA"
                    : "✦ Generate"
                  )}
                </button>
              )}
            </div>

            {/* OUTPUT PANEL */}
            <div className={`output-panel ${loading ? "pulsing" : ""}`}>
              {error && <div className="error-bar">{error}</div>}
              {!loading && <ComplianceBanner flags={complianceFlags} onDismiss={() => setComplianceFlags([])} />}

              {!currentResult && !loading && (
                <div className="empty-output">
                  <div className="empty-icon">✦</div>
                  <div className="empty-title">Your output will appear here</div>
                  <div className="empty-sub">
                    {tab === "contract" ? "Paste a contract to get a simplified breakdown" : "Fill in the details and hit generate"}
                  </div>
                </div>
              )}

              {loading && (
                <div className="empty-output">
                  <div className="spinner" style={{width:32,height:32,borderWidth:3,marginBottom:16}} />
                  <div className="empty-title">Generating...</div>
                  <div className="empty-sub">This usually takes 5–10 seconds</div>
                </div>
              )}

              {/* LISTING OUTPUT */}
              {tab === "listing" && listingResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />MLS Listing</div>
                    <div style={{display:'flex',gap:6}}>
                      {["mls","social","headlines","neighborhood"].map(t => (
                        <button key={t} className={`pill ${outTab === t ? "on" : ""}`} onClick={() => setOutTab(t)} style={{fontSize:11,padding:'3px 10px'}}>
                          {t === "mls" ? "Full Listing" : t === "social" ? "Social" : t === "headlines" ? "Headlines" : "Neighborhood"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="outbox-body">
                    {outTab === "mls" && renderMlsText(listingResult.mls)}
                    {outTab === "social" && <OutText>{listingResult.social}</OutText>}
                    {outTab === "headlines" && <OutText style={{whiteSpace:'pre-wrap'}}>{listingResult.headlines}</OutText>}
                    {outTab === "neighborhood" && <OutText>{listingResult.neighborhood || "Generating neighborhood description..."}</OutText>}
                    <div className="out-meta">
                      Words: <strong>{(outTab === "mls" ? listingResult.mls : outTab === "social" ? listingResult.social : outTab === "headlines" ? listingResult.headlines : listingResult.neighborhood || "")?.split(/\s+/).filter(Boolean).length || 0}</strong>
                      {activeToneLabel && <> &nbsp;·&nbsp; Tone: <strong>{activeToneLabel}</strong></>}
                    </div>
                    {outTab === "mls" && (
                      <div className="mls-counter">
                        <select className="mls-board-select" value={mlsBoard} onChange={(e) => setMlsBoard(e.target.value)} aria-label="MLS board character limit">
                          {MLS_LIMITS.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                        </select>
                        <span className="mls-count" style={{ color: (listingResult.mls || "").length > mlsLimit ? "var(--red)" : (listingResult.mls || "").length > mlsLimit * 0.8 ? "#f59e0b" : "var(--green)" }}>
                          {(listingResult.mls || "").length.toLocaleString()} / {mlsLimit.toLocaleString()} chars
                        </span>
                        {(listingResult.mls || "").length > mlsLimit && (
                          <button className="act-btn" onClick={trimListing} disabled={trimming}>{trimming ? "Trimming…" : "Trim to fit"}</button>
                        )}
                      </div>
                    )}
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="act-btn" onClick={() => copy(outTab === "mls" ? listingResult.mls : outTab === "social" ? listingResult.social : outTab === "headlines" ? listingResult.headlines : listingResult.neighborhood)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Listing" text={outTab === "mls" ? listingResult.mls : outTab === "social" ? listingResult.social : outTab === "headlines" ? listingResult.headlines : listingResult.neighborhood} />
                      <button className="act-btn" onClick={run}>Regenerate</button>
                      <button className="act-btn" onClick={() => printableExport("MLS Listing", outTab === "mls" ? listingResult.mls : outTab === "social" ? listingResult.social : outTab === "headlines" ? listingResult.headlines : listingResult.neighborhood, { address })}>Save PDF</button>
                    </div>
                  </div>
                </div>
              )}

              {/* FOLLOWUP OUTPUT */}
              {tab === "followup" && emailResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Follow-Up Emails</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="followup-timeline">
                    {emailResult.map((email, i) => {
                      const day = email.day || [1, 3, 7][i] || (i + 1);
                      return (
                      <div key={i} className={`followup-step ${i === emailResult.length - 1 ? "last" : ""}`}>
                        <div className="followup-rail"><span className="followup-dot" /></div>
                        <div className="email-card" style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:16}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                            <span className="followup-day">Day {day} &middot; <span style={{color:'var(--accent)',textTransform:'uppercase',letterSpacing:'0.5px',fontSize:11}}>{email.tag}</span></span>
                            <div style={{display:'flex',gap:6}}>
                              <button className="act-btn" style={{fontSize:11}} onClick={() => copy(`Subject: ${email.subject}\n\n${email.body}`)}>Copy</button>
                              <ShareBtn title="Email" style={{fontSize:11}} text={`Subject: ${email.subject}\n\n${email.body}`} />
                            </div>
                          </div>
                          <div className="out-subject"><strong>Subject:</strong> {email.subject}</div>
                          <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{email.body}</OutText>
                          {renderSendEmail(email.subject, email.body)}
                        </div>
                      </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              )}

              {/* CONTRACT OUTPUT */}
              {tab === "contract" && contractResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Simplified Summary</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    {contractResult.map((item, i) => (
                      <div key={i} style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14,marginBottom:10}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                          <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',padding:'2px 6px',borderRadius:4,letterSpacing:'0.5px',background: item.tag === 'flag' ? 'rgba(239,68,68,0.15)' : item.tag === 'date' ? 'rgba(59,130,246,0.15)' : item.tag === 'contingency' ? 'rgba(234,179,8,0.15)' : 'rgba(100,100,100,0.15)',color: item.tag === 'flag' ? '#ef4444' : item.tag === 'date' ? '#60a5fa' : item.tag === 'contingency' ? '#eab308' : '#888'}}>{item.tag}</span>
                          <span style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>{item.label}</span>
                        </div>
                        <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{item.text}</div>
                      </div>
                    ))}
                    <button className="act-btn" style={{marginTop:8}} onClick={() => copy(contractResult.map(i => `[${i.tag}] ${i.label}: ${i.text}`).join('\n\n'))}>Copy All</button>
                    <ShareBtn title="Contract Summary" style={{marginTop:8}} text={contractResult.map(i => `[${i.tag}] ${i.label}: ${i.text}`).join('\n\n')} />
                    <button className="act-btn" style={{marginTop:8}} onClick={() => printableExport("Contract Summary", contractResult.map(i => `[${i.tag}] ${i.label}: ${i.text}`).join('\n\n'))}>Save PDF</button>
                  </div>
                </div>
              )}

              {/* INSPECTION OUTPUT */}
              {tab === "inspection" && inspectionResult && !loading && (() => {
                const items = Array.isArray(inspectionResult.items) ? inspectionResult.items : [];
                const repairAsk = inspectionResult.repair_ask || "";
                const sevColor = (t) => t === "safety" ? "#ef4444" : t === "major" ? "#f59e0b" : "#9ca3af";
                const sevBg = (t) => t === "safety" ? "rgba(239,68,68,0.15)" : t === "major" ? "rgba(245,158,11,0.15)" : "rgba(100,100,100,0.15)";
                const fullText = [items.map(i => `[${(i.tag || "").toUpperCase()}] ${i.label}: ${i.text}`).join("\n\n"), repairAsk ? `\nREPAIR ASK:\n${repairAsk}` : ""].filter(Boolean).join("\n");
                return (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Inspection Summary</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    {items.map((item, i) => (
                      <div key={i} style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:14,marginBottom:10}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                          <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',padding:'2px 6px',borderRadius:4,letterSpacing:'0.5px',background:sevBg(item.tag),color:sevColor(item.tag)}}>{item.tag}</span>
                          <span style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>{item.label}</span>
                        </div>
                        <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{item.text}</div>
                      </div>
                    ))}
                    {repairAsk && (
                      <div style={{background:'rgba(99,102,241,0.06)',border:'1px solid #2a2a2a',borderRadius:10,padding:14,marginBottom:10}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:6}}>
                          <span style={{fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--accent)'}}>Negotiation-ready repair ask</span>
                          <button className="act-btn" style={{fontSize:11}} onClick={() => copy(repairAsk)}>Copy</button>
                        </div>
                        <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.5,whiteSpace:'pre-wrap'}}>{repairAsk}</div>
                      </div>
                    )}
                    <div style={{fontSize:11,color:'var(--text3)',lineHeight:1.5,marginBottom:8}}>A summary of the inspector's report to help you triage and negotiate — not a home inspection, a repair estimate, or professional advice. Always read the full report.</div>
                    <button className="act-btn" style={{marginTop:8}} onClick={() => copy(fullText)}>Copy All</button>
                    <ShareBtn title="Inspection Summary" style={{marginTop:8}} text={fullText} />
                    <button className="act-btn" style={{marginTop:8}} onClick={() => printableExport("Inspection Summary", fullText)}>Save PDF</button>
                  </div>
                </div>
                );
              })()}

              {/* OFFER SUMMARY OUTPUT */}
              {tab === "offer" && offerResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Offer Summary</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="out-subject"><strong>Subject:</strong> {offerResult.subject}</div>
                    <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{offerResult.body}</OutText>
                    <div style={{fontSize:11,color:'var(--text3)',lineHeight:1.5,marginTop:12}}>Fact-only offer cover for the listing agent. Review before sending — never add the buyer's family, religion, or personal details. Any Fair Housing flags appear above. Not legal advice.</div>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(`Subject: ${offerResult.subject}\n\n${offerResult.body}`)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Offer Summary" text={`Subject: ${offerResult.subject}\n\n${offerResult.body}`} />
                      <button className="act-btn" onClick={() => printableExport("Offer Summary", `Subject: ${offerResult.subject}\n\n${offerResult.body}`)}>Save PDF</button>
                    </div>
                    {renderSendEmail(offerResult.subject, offerResult.body)}
                  </div>
                </div>
              )}

              {/* OPEN HOUSE OUTPUT */}
              {tab === "openhouse" && openHouseResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Open House Follow-Up</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="out-subject"><strong>Subject:</strong> {openHouseResult.subject}</div>
                    <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{openHouseResult.body}</OutText>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(`Subject: ${openHouseResult.subject}\n\n${openHouseResult.body}`)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Open House Follow-Up" text={`Subject: ${openHouseResult.subject}\n\n${openHouseResult.body}`} />
                      <button className="act-btn" onClick={() => printableExport("Open House Follow-Up", `Subject: ${openHouseResult.subject}\n\n${openHouseResult.body}`)}>Save PDF</button>
                    </div>
                    {renderSendEmail(openHouseResult.subject, openHouseResult.body)}
                  </div>
                </div>
              )}

              {/* SELLER UPDATE OUTPUT */}
              {tab === "sellerupdate" && sellerUpdateResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Seller Update</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="out-subject"><strong>Subject:</strong> {sellerUpdateResult.subject}</div>
                    <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{sellerUpdateResult.body}</OutText>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(`Subject: ${sellerUpdateResult.subject}\n\n${sellerUpdateResult.body}`)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Seller Update" text={`Subject: ${sellerUpdateResult.subject}\n\n${sellerUpdateResult.body}`} />
                      <button className="act-btn" onClick={() => printableExport("Seller Update", `Subject: ${sellerUpdateResult.subject}\n\n${sellerUpdateResult.body}`)}>Save PDF</button>
                    </div>
                    {renderSendEmail(sellerUpdateResult.subject, sellerUpdateResult.body)}
                    {sellerUpdateResult.price_drop_email && (
                      <div style={{marginTop:20,borderTop:'1px solid var(--border)',paddingTop:16}}>
                        <div className="outbox-label" style={{marginBottom:8}}><div className="status-dot" style={{background:'#ef4444'}} />Price Reduction Email</div>
                        <div className="out-subject"><strong>Subject:</strong> {sellerUpdateResult.price_drop_email.subject}</div>
                        <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{sellerUpdateResult.price_drop_email.body}</OutText>
                        <div style={{display:'flex',gap:8,marginTop:12}}>
                          <button className="act-btn" onClick={() => copy(`Subject: ${sellerUpdateResult.price_drop_email.subject}\n\n${sellerUpdateResult.price_drop_email.body}`)}>Copy</button>
                          <ShareBtn title="Price Drop Email" text={`Subject: ${sellerUpdateResult.price_drop_email.subject}\n\n${sellerUpdateResult.price_drop_email.body}`} />
                        </div>
                        {renderSendEmail(sellerUpdateResult.price_drop_email.subject, sellerUpdateResult.price_drop_email.body)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* BIO OUTPUT */}
              {tab === "bio" && bioResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Agent Bio</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className={`pill ${bioTab === "long" ? "on" : ""}`} onClick={() => setBioTab("long")} style={{fontSize:11,padding:'3px 10px'}}>Full Bio</button>
                      <button className={`pill ${bioTab === "short" ? "on" : ""}`} onClick={() => setBioTab("short")} style={{fontSize:11,padding:'3px 10px'}}>Short Bio</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <OutText style={{whiteSpace:'pre-wrap'}}>{bioTab === "long" ? bioResult.long : bioResult.short}</OutText>
                    <div className="out-meta">
                      Words: <strong>{(bioTab === "long" ? bioResult.long : bioResult.short)?.split(/\s+/).filter(Boolean).length || 0}</strong>
                      &nbsp;·&nbsp; Tone: <strong>{bioTone}</strong>
                    </div>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="act-btn" onClick={() => copy(bioTab === "long" ? bioResult.long : bioResult.short)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Agent Bio" text={bioTab === "long" ? bioResult.long : bioResult.short} />
                      <button className="act-btn" onClick={run}>Regenerate</button>
                      <button className="act-btn" onClick={() => printableExport("Agent Bio", bioTab === "long" ? bioResult.long : bioResult.short)}>Save PDF</button>
                    </div>
                  </div>
                </div>
              )}

              {/* EXPIRED OUTPUT */}
              {tab === "expired" && expiredResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Expired Listing Outreach</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="out-subject"><strong>Subject:</strong> {expiredResult.subject}</div>
                    <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{expiredResult.body}</OutText>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(`Subject: ${expiredResult.subject}\n\n${expiredResult.body}`)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="Expired Listing Outreach" text={`Subject: ${expiredResult.subject}\n\n${expiredResult.body}`} />
                      <button className="act-btn" onClick={() => printableExport("Expired Listing Outreach", `Subject: ${expiredResult.subject}\n\n${expiredResult.body}`)}>Save PDF</button>
                    </div>
                    {renderSendEmail(expiredResult.subject, expiredResult.body)}
                  </div>
                </div>
              )}

              {/* FSBO OUTPUT */}
              {tab === "fsbo" && fsboResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />FSBO Outreach</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div className="out-subject"><strong>Subject:</strong> {fsboResult.subject}</div>
                    <OutText style={{marginTop:8,whiteSpace:'pre-wrap'}}>{fsboResult.body}</OutText>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(`Subject: ${fsboResult.subject}\n\n${fsboResult.body}`)}>{copied ? "✓ Copied" : "Copy"}</button>
                      <ShareBtn title="FSBO Outreach" text={`Subject: ${fsboResult.subject}\n\n${fsboResult.body}`} />
                      <button className="act-btn" onClick={() => printableExport("FSBO Outreach", `Subject: ${fsboResult.subject}\n\n${fsboResult.body}`)}>Save PDF</button>
                    </div>
                    {renderSendEmail(fsboResult.subject, fsboResult.body)}
                  </div>
                </div>
              )}

              {/* CMA OUTPUT */}
              {tab === "cma" && cmaResult && !loading && (
                <div className="outbox">
                  <div className="outbox-header">
                    <div className="outbox-label"><div className="status-dot" />Comparative Market Analysis</div>
                    <div style={{display:'flex',gap:6}}>
                      <button className="act-btn" onClick={run}>Regenerate</button>
                    </div>
                  </div>
                  <div className="outbox-body">
                    <div style={{display:'flex',gap:16,marginBottom:14,flexWrap:'wrap'}}>
                      <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:'12px 16px',flex:1,minWidth:140}}>
                        <div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4}}>Value Range</div>
                        <div style={{fontSize:18,fontWeight:700,color:'var(--accent)'}}>{cmaResult.value_range}</div>
                      </div>
                      <div style={{background:'#111',border:'1px solid #1f1f1f',borderRadius:10,padding:'12px 16px',flex:1,minWidth:140}}>
                        <div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:4}}>{cmaResult.mode === "buyer" ? "Recommended Offer" : "Recommended Price"}</div>
                        <div style={{fontSize:18,fontWeight:700,color:'var(--text)'}}>{fmtRec(cmaResult.recommended_price)}</div>
                      </div>
                    </div>
                    {cmaResult.market_data && (
                      <div style={{fontSize:11,color:'var(--green)',marginBottom:10,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>✓ Grounded in live RentCast market data{cmaResult.lookups_used != null && cmaResult.lookups_limit != null ? ` · ${cmaResult.lookups_used}/${cmaResult.lookups_limit} lookups used this month` : ""}</div>
                    )}
                    {cmaResult.market_note && (
                      <div style={{background:'#1a1700',border:'1px solid #f59e0b',borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',alignItems:'flex-start',gap:8}}>
                        <span>ℹ️</span><p style={{fontSize:12,color:'#f59e0b',margin:0,lineHeight:1.5}}>{cmaResult.market_note}</p>
                      </div>
                    )}
                    <div className="out-text">{cmaResult.summary}</div>
                    {cmaResult.market_data && Array.isArray(cmaResult.comparables) && cmaResult.comparables.length > 0 && (
                      <div style={{marginTop:14}}>
                        <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6}}>Comparable Sales</div>
                        <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
                          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:440}}>
                            <thead>
                              <tr style={{color:'var(--text3)',textAlign:'left'}}>
                                <th style={{padding:'6px 8px',fontWeight:600}}>Address</th>
                                <th style={{padding:'6px 8px',fontWeight:600}}>Price</th>
                                <th style={{padding:'6px 8px',fontWeight:600}}>Bd/Ba</th>
                                <th style={{padding:'6px 8px',fontWeight:600}}>Sqft</th>
                                <th style={{padding:'6px 8px',fontWeight:600}}>$/sqft</th>
                                <th style={{padding:'6px 8px',fontWeight:600}}>Dist</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dedupeComps(cmaResult.comparables).map((c, i) => (
                                <tr key={i} style={{borderTop:'1px solid #1f1f1f',color:'var(--text2)'}}>
                                  <td style={{padding:'6px 8px'}}>{c.address || "—"}</td>
                                  <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{c.price != null ? `$${Number(c.price).toLocaleString()}` : "—"}</td>
                                  <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{(c.bedrooms ?? "—")}/{(c.bathrooms ?? "—")}</td>
                                  <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{c.sqft ? Number(c.sqft).toLocaleString() : "—"}</td>
                                  <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{c.sqft && c.price != null ? `$${Math.round(c.price / c.sqft).toLocaleString()}` : "—"}</td>
                                  <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{c.distance != null ? `${c.distance} mi` : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {cmaResult.key_factors && cmaResult.key_factors.length > 0 && (
                      <div style={{marginTop:14}}>
                        <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6}}>Key Factors</div>
                        {cmaResult.key_factors.map((f, i) => (
                          <div key={i} style={{fontSize:13,color:'var(--text2)',padding:'4px 0',borderBottom:'1px solid #1f1f1f'}}>• {f}</div>
                        ))}
                      </div>
                    )}
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="act-btn" onClick={() => copy(cmaExportText(cmaResult))}>{copied ? "✓ Copied" : "Copy"}</button>
                      <button className="act-btn" onClick={handleCmaSavePdf} disabled={cmaPdfLoading}>{cmaPdfLoading ? "Building report…" : "Save PDF"}</button>
                    </div>
                  </div>
                </div>
              )}

              {renderHistory()}
            </div>
          </div>}
        </div>
      </div>
    </>
  );
}
